/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import config from "../../../../config.json";
import { fetchAllMenus } from "../../../services/MenuService";
import {
  deleteEvent,
  fetchAllEvents,
  republishEvent,
  saveEvent,
  updateEvent,
  updateEventArchive,
  updateEventsOrder,
} from "../../../services/EventService";
import {
  IMAGE_PICKER_ACCEPT,
  validateImageFile,
} from "../../../utils/imageValidation";
import {
  promptEventFullClientOptions,
  resetEventFullClientOptions,
} from "./eventFullClientOptions";
import { confirmEventStatusChange, showEventActionSuccess } from "./eventDialogs";
import "./events.css";

const PAGE_SIZE = 8;
const TABLE_MENUS_PREVIEW_LIMIT = 3;

const sortEventsByPriority = (list) =>
  [...list].sort((a, b) => {
    const aSpot = Boolean(a.isSpotlight);
    const bSpot = Boolean(b.isSpotlight);
    if (aSpot !== bSpot) return aSpot ? -1 : 1;
    const priorityDiff = (Number(b.priority) || 0) - (Number(a.priority) || 0);
    if (priorityDiff !== 0) return priorityDiff;
    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
  });

const EMPTY_FORM = {
  title: "",
  message: "",
  capacity: "",
  menuFKs: [],
  startDate: "",
  endDate: "",
  publishBeforeAmount: 0,
  publishBeforeUnit: "days",
  publishUntil: "",
  isRecurring: false,
  recurrenceType: "weekly",
  recurrenceWeekday: 0,
  recurrenceMonthDay: 1,
  recurrenceMonths: [],
  recurrenceMonthDays: [],
  isSpotlight: false,
  isActive: true,
  clientPosition: "",
};

const getClientPositionForEvent = (event, eventsList) => {
  if (!event || event.isSpotlight) return "";
  const ordered = sortEventsByPriority(eventsList).filter((item) => !item.isSpotlight);
  const index = ordered.findIndex((item) => String(item._id) === String(event._id));
  return index >= 0 ? String(index + 1) : "";
};

const getMaxClientPosition = (eventsList, isEditing) => {
  const count = eventsList.filter((item) => !item.isSpotlight).length;
  return isEditing ? Math.max(1, count) : Math.max(1, count + 1);
};

const WEEKDAY_OPTIONS = [
  { value: 0, key: "eventsWeekdaySun" },
  { value: 1, key: "eventsWeekdayMon" },
  { value: 2, key: "eventsWeekdayTue" },
  { value: 3, key: "eventsWeekdayWed" },
  { value: 4, key: "eventsWeekdayThu" },
  { value: 5, key: "eventsWeekdayFri" },
  { value: 6, key: "eventsWeekdaySat" },
];

const HIGH_OCCURRENCE_THRESHOLD = 5;

const toDatetimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatDateRange = (start, end) => {
  const opts = { day: "2-digit", month: "short", year: "numeric" };
  return `${new Date(start).toLocaleDateString(undefined, opts)} – ${new Date(end).toLocaleDateString(undefined, opts)}`;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const opts = { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" };
  return date.toLocaleString(undefined, opts);
};

const normalizePublishBeforeUnit = (unit) =>
  unit === "weeks" || unit === "months" ? unit : "days";

const getPublishDate = (startDate, amount = 0, unit = "days") => {
  if (!startDate) return null;
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return null;
  const parsed = Math.max(0, Math.floor(Number(amount) || 0));
  const publish = new Date(start);
  if (unit === "months") {
    publish.setMonth(publish.getMonth() - parsed);
  } else {
    const multiplier = unit === "weeks" ? 7 : 1;
    publish.setDate(publish.getDate() - parsed * multiplier);
  }
  return publish;
};

const getEventPublishDate = (event) =>
  getPublishDate(event?.startDate, event?.publishBeforeAmount, event?.publishBeforeUnit);

const getEventUnpublishDate = (event) => {
  if (event?.publishUntil) {
    const until = new Date(event.publishUntil);
    if (!Number.isNaN(until.getTime())) return until;
  }
  if (event?.endDate) {
    const end = new Date(event.endDate);
    if (!Number.isNaN(end.getTime())) return end;
  }
  return null;
};

const PUBLISH_BEFORE_PRESETS = [
  { amount: 0, unit: "days", key: "eventsPublishPresetAtStart" },
  { amount: 1, unit: "days", key: "eventsPublishPreset1Day" },
  { amount: 3, unit: "days", key: "eventsPublishPreset3Days" },
  { amount: 1, unit: "weeks", key: "eventsPublishPreset1Week" },
  { amount: 2, unit: "weeks", key: "eventsPublishPreset2Weeks" },
  { amount: 1, unit: "months", key: "eventsPublishPreset1Month" },
];

const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => index + 1);

const MONTH_OPTIONS = [
  { value: 1, key: "eventsMonthJan" },
  { value: 2, key: "eventsMonthFeb" },
  { value: 3, key: "eventsMonthMar" },
  { value: 4, key: "eventsMonthApr" },
  { value: 5, key: "eventsMonthMay" },
  { value: 6, key: "eventsMonthJun" },
  { value: 7, key: "eventsMonthJul" },
  { value: 8, key: "eventsMonthAug" },
  { value: 9, key: "eventsMonthSep" },
  { value: 10, key: "eventsMonthOct" },
  { value: 11, key: "eventsMonthNov" },
  { value: 12, key: "eventsMonthDec" },
];

const getMonthFromDate = (value) => {
  if (!value) return 1;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 1 : date.getMonth() + 1;
};

const normalizeRecurrenceIntArray = (value, min, max) => {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value
        .map((item) => Math.floor(Number(item)))
        .filter((item) => Number.isFinite(item) && item >= min && item <= max)
    ),
  ].sort((a, b) => a - b);
};

const getRecurrenceMonths = (eventOrForm) => {
  const fromArray = normalizeRecurrenceIntArray(eventOrForm?.recurrenceMonths, 1, 12);
  if (fromArray.length) return fromArray;
  return [getMonthFromDate(eventOrForm?.startDate)];
};

const getRecurrenceMonthDaysList = (eventOrForm) => {
  const fromArray = normalizeRecurrenceIntArray(eventOrForm?.recurrenceMonthDays, 1, 31);
  if (fromArray.length) return fromArray;
  return [getRecurrenceMonthDay(eventOrForm)];
};

const toggleRecurrenceValue = (values, value) => {
  const next = new Set(values);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return [...next].sort((a, b) => a - b);
};

const formatMonthList = (months, t) =>
  months
    .map((month) => {
      const option = MONTH_OPTIONS.find((item) => item.value === month);
      return option ? t(option.key) : String(month);
    })
    .join(", ");

const getDayFromDate = (value) => {
  if (!value) return 1;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 1 : date.getDate();
};

const getRecurrenceMonthDay = (eventOrForm) => {
  const raw = eventOrForm?.recurrenceMonthDay;
  if (Number.isFinite(Number(raw))) return Math.min(31, Math.max(1, Number(raw)));
  return getDayFromDate(eventOrForm?.startDate);
};

const getRecurrenceType = (eventOrForm) => {
  const type = String(eventOrForm?.recurrenceType || "weekly").toLowerCase();
  if (type === "monthly") return "custom";
  if (type === "daily" || type === "custom") return type;
  return "weekly";
};

const getStoredRecurrenceType = (eventOrForm) =>
  String(eventOrForm?.recurrenceType || "weekly").toLowerCase();

const getRecurrenceFormFields = (eventOrForm) => {
  const storedType = getStoredRecurrenceType(eventOrForm);
  if (storedType === "monthly") {
    return {
      recurrenceType: "custom",
      recurrenceMonths: MONTH_OPTIONS.map((month) => month.value),
      recurrenceMonthDays: [getRecurrenceMonthDay(eventOrForm)],
    };
  }
  return {
    recurrenceType: getRecurrenceType(eventOrForm),
    recurrenceMonths: getRecurrenceMonths(eventOrForm),
    recurrenceMonthDays: getRecurrenceMonthDaysList(eventOrForm),
  };
};

const getWeekdayFromDate = (value) => {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getDay();
};

const getRecurrenceWeekday = (eventOrForm) => {
  const raw = eventOrForm?.recurrenceWeekday;
  if (Number.isFinite(Number(raw))) return Number(raw);
  return getWeekdayFromDate(eventOrForm?.startDate);
};

const formatRecurrenceLabel = (eventOrForm, t) => {
  if (!eventOrForm?.isRecurring) return "";
  const type = getRecurrenceType(eventOrForm);
  if (type === "daily") return t("eventsRecurrenceDaily");
  if (type === "custom" || getStoredRecurrenceType(eventOrForm) === "monthly") {
    if (getStoredRecurrenceType(eventOrForm) === "monthly") {
      return t("eventsRecurrenceMonthly", { day: getRecurrenceMonthDay(eventOrForm) });
    }
    const months = getRecurrenceMonths(eventOrForm);
    const days = getRecurrenceMonthDaysList(eventOrForm);
    return t("eventsRecurrenceCustom", {
      months: formatMonthList(months, t),
      days: days.join(", "),
    });
  }
  const weekday = WEEKDAY_OPTIONS.find((item) => item.value === getRecurrenceWeekday(eventOrForm));
  return t("eventsRecurrenceWeekly", { day: weekday ? t(weekday.key) : "" });
};

const canRepublishEvent = (event) =>
  Boolean(event?.isRecurring) && !event?.archived && new Date(event.endDate).getTime() < Date.now();

const isHighOccurrence = (event) => Number(event?.occurrenceCount) >= HIGH_OCCURRENCE_THRESHOLD;

const isEventPastUnpublish = (event) => {
  const until = getEventUnpublishDate(event);
  return Boolean(until && until.getTime() < Date.now());
};

const isEventLive = (event) => {
  if (!event?.isActive || event?.archived || isEventPastUnpublish(event)) return false;
  const now = Date.now();
  return now >= new Date(event.startDate).getTime() && now <= new Date(event.endDate).getTime();
};

const isEventPendingPublish = (event) => {
  if (!event?.isActive || event?.archived || isEventPastUnpublish(event)) return false;
  const publishAt = getEventPublishDate(event);
  if (!publishAt) return false;
  return Date.now() < publishAt.getTime();
};

const isEventScheduled = (event) => {
  if (!event?.isActive || event?.archived || isEventPastUnpublish(event)) return false;
  if (isEventPendingPublish(event)) return false;
  return new Date(event.startDate).getTime() > Date.now();
};

const isEventUpcoming = (event) => isEventScheduled(event) || isEventPendingPublish(event);

const isEventInactive = (event) => {
  if (event?.archived) return false;
  if (!event?.isActive) return true;
  return !isEventLive(event) && !isEventScheduled(event) && !isEventPendingPublish(event);
};

const isEventAlmostFull = (event) => event?.isAlmostFull === true;

const isEventFull = (event) => {
  if (event?.isManuallyFull === true) return true;
  if (event?.isFull === true) return true;
  const capacity =
    event?.capacity != null && Number(event.capacity) > 0
      ? Math.floor(Number(event.capacity))
      : null;
  if (capacity == null) return false;
  const confirmedGuests = Math.max(0, Math.floor(Number(event.confirmedGuests) || 0));
  const remainingPlaces =
    event?.remainingPlaces != null
      ? Math.max(0, Math.floor(Number(event.remainingPlaces)))
      : Math.max(0, capacity - confirmedGuests);
  return remainingPlaces === 0;
};

const isEventAlmostFullStatus = (event) => {
  if (event?.archived || event?.isActive === false) return false;
  if (!isEventAlmostFull(event) || isEventFull(event)) return false;
  return isEventLive(event) || isEventScheduled(event) || isEventPendingPublish(event);
};

const isEventFullStatus = (event) => {
  if (event?.archived || event?.isActive === false) return false;
  if (!isEventFull(event)) return false;
  return isEventLive(event) || isEventScheduled(event) || isEventPendingPublish(event);
};

const getEventDisplayStatusKey = (event) => {
  if (!event?.isActive) return "hidden";
  if (isEventPendingPublish(event)) {
    if (isEventFull(event)) return "full";
    if (isEventAlmostFull(event)) return "almost_full";
    return "pending";
  }
  if (isEventLive(event)) {
    if (isEventFull(event)) return "full";
    if (isEventAlmostFull(event)) return "almost_full";
    return "live";
  }
  if (isEventScheduled(event)) {
    if (isEventFull(event)) return "full";
    if (isEventAlmostFull(event)) return "almost_full";
    return "scheduled";
  }
  return "ended";
};

const EVENT_STATUS_FILTER_OPTIONS = [
  { id: "all", labelKey: "eventsFilterAll" },
  { id: "live", labelKey: "eventsStatusLive" },
  { id: "scheduled", labelKey: "eventsStatusScheduled" },
  { id: "pending", labelKey: "eventsStatusPendingPublish" },
  { id: "almost_full", labelKey: "eventsStatusAlmostFull" },
  { id: "full", labelKey: "eventsStatusFull" },
  { id: "hidden", labelKey: "eventsStatusInactive" },
  { id: "ended", labelKey: "eventsStatusEnded" },
];

const getEventMenuIds = (event) =>
  (event?.menuFKs || [])
    .map((menu) => String(typeof menu === "object" ? menu._id : menu))
    .filter(Boolean);

const getMenuLabel = (menu, lang = "en") => {
  const details = menu?.menuDetails || [];
  const localized = details.find((d) => d?.language === lang)?.menuName;
  const first = details.find((d) => d?.menuName)?.menuName;
  return localized || first || "Menu";
};

const getUploadBases = () => {
  const bases = [];
  const cdn = (config.UPLOADS_SERVER || "").replace(/\/+$/, "");
  if (cdn) bases.push(cdn);

  const origin = (config.UPLOADS_SERVER_QR || "").replace(/\/+$/, "");
  if (origin) {
    bases.push(`${origin}/uploads`);
    bases.push(`${origin}/upload`);
    bases.push(origin);
  }

  return [...new Set(bases.filter(Boolean))];
};

const buildUploadUrl = (base, type, filename) => {
  const cleanBase = String(base).replace(/\/+$/, "");
  const encoded = String(filename)
    .split("/")
    .filter(Boolean)
    .map((part) => encodeURIComponent(part))
    .join("/");
  return `${cleanBase}/${type}/${encoded}`;
};

const EVENT_IMAGE_FOLDERS = ["event", "restaurant"];

const parseStoredImageRef = (imageUrl) => {
  if (!imageUrl) return { filename: "", folder: null };
  const raw = String(imageUrl).trim().replace(/\\/g, "/");

  if (/^https?:\/\//i.test(raw)) {
    try {
      const parts = new URL(raw).pathname.split("/").filter(Boolean);
      const filename = parts[parts.length - 1] || "";
      const folder = EVENT_IMAGE_FOLDERS.find((type) => parts.includes(type)) || null;
      return { filename, folder };
    } catch {
      return { filename: "", folder: null };
    }
  }

  if (raw.includes("/")) {
    const parts = raw.split("/").filter(Boolean);
    const filename = parts[parts.length - 1] || "";
    const folder = EVENT_IMAGE_FOLDERS.find((type) => parts.includes(type)) || null;
    return { filename, folder };
  }

  return { filename: raw, folder: null };
};

const getEventImageCandidates = (imageUrl) => {
  const { filename, folder } = parseStoredImageRef(imageUrl);
  if (!filename) return [];

  const folders = folder
    ? [folder, ...EVENT_IMAGE_FOLDERS.filter((type) => type !== folder)]
    : EVENT_IMAGE_FOLDERS;

  const bases = getUploadBases();
  const urls = folders.flatMap((type) => bases.map((base) => buildUploadUrl(base, type, filename)));

  return urls.filter((url, index) => url && urls.indexOf(url) === index);
};

const resolveEventImage = (imageUrl) => getEventImageCandidates(imageUrl)[0] || "";

const buildEventShareUrl = (eventId) => {
  if (!eventId) return "";
  const apiBase = String(config.dev || "").replace(/\/+$/, "");
  if (!apiBase) return "";
  return `${apiBase}/events/share/${encodeURIComponent(eventId)}`;
};

const parseStoredMenuPhoto = (photo) => {
  if (!photo) return "";
  const raw = String(photo).trim().replace(/\\/g, "/");
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.includes("/")) {
    const parts = raw.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  }
  return raw;
};

const getMenuImageCandidates = (photo) => {
  const raw = String(photo || "").trim();
  if (!raw) return [];
  if (/^https?:\/\//i.test(raw)) return [raw];

  const filename = parseStoredMenuPhoto(raw);
  if (!filename) return [];

  const bases = getUploadBases();
  const urls = bases.map((base) => buildUploadUrl(base, "menu", filename));
  return urls.filter((url, index) => url && urls.indexOf(url) === index);
};

function UploadThumb({ candidates, className, placeholderClassName }) {
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setCandidateIndex(0);
    setFailed(false);
  }, [candidates]);

  if (!candidates?.length || failed || candidateIndex >= candidates.length) {
    return (
      <span className={placeholderClassName}>
        <i className="fa-regular fa-image" />
      </span>
    );
  }

  return (
    <img
      src={candidates[candidateIndex]}
      alt=""
      className={className}
      onError={() => {
        if (candidateIndex < candidates.length - 1) {
          setCandidateIndex((index) => index + 1);
        } else {
          setFailed(true);
        }
      }}
    />
  );
}

function EventThumb({ imageUrl }) {
  const candidates = useMemo(() => getEventImageCandidates(imageUrl), [imageUrl]);

  return (
    <UploadThumb
      candidates={candidates}
      className="events-event-cell__thumb"
      placeholderClassName="events-event-cell__thumb events-event-cell__thumb--placeholder"
    />
  );
}

function MenuThumb({ photo, className = "events-menu-tile__img", placeholderClassName = "events-menu-tile__img events-menu-tile__img--placeholder" }) {
  const candidates = useMemo(() => getMenuImageCandidates(photo), [photo]);

  if (!photo) {
    return (
      <span className={placeholderClassName}>
        <i className="fa-solid fa-utensils" />
      </span>
    );
  }

  return (
    <UploadThumb
      candidates={candidates}
      className={className}
      placeholderClassName={placeholderClassName}
    />
  );
}

const durationPresets = (days, startDateValue) => {
  let start;
  if (startDateValue) {
    const parsed = new Date(startDateValue);
    if (!Number.isNaN(parsed.getTime())) {
      start = parsed;
    }
  }
  if (!start) {
    start = new Date();
    start.setMinutes(0, 0, 0);
  }

  const end = new Date(start);
  if (days === 0) {
    end.setHours(23, 59, 0, 0);
  } else if (days === 30) {
    end.setMonth(end.getMonth() + 1);
  } else {
    end.setDate(end.getDate() + days);
  }

  return { startDate: toDatetimeLocal(start), endDate: toDatetimeLocal(end) };
};

function EventLivePreview({ form, imagePreview, storedImageUrl, menus, lang, t }) {
  const selectedMenus = (form.menuFKs || [])
    .map((id) => menus.find((m) => String(m._id) === String(id)))
    .filter(Boolean);

  const previewCandidates = useMemo(() => {
    if (imagePreview?.startsWith("blob:")) return [imagePreview];
    const storedCandidates = getEventImageCandidates(storedImageUrl);
    if (storedCandidates.length) return storedCandidates;
    return imagePreview ? [imagePreview] : [];
  }, [imagePreview, storedImageUrl]);

  return (
    <aside className="events-preview-panel" aria-label={t("eventsFormPreviewLabel")}>
      <div className="events-preview-panel__label">
        <i className="fa-regular fa-eye" />
        {t("eventsFormPreviewLabel")}
      </div>
      <div className="events-preview-card">
        <div className="events-preview-card__cover">
          {previewCandidates.length ? (
            <UploadThumb
              candidates={previewCandidates}
              className=""
              placeholderClassName="events-preview-card__cover-placeholder"
            />
          ) : (
            <div className="events-preview-card__cover-placeholder">
              <i className="fa-regular fa-image" />
              <span>{t("eventsFormPreviewEmptyImage")}</span>
            </div>
          )}
          {form.isActive ? (
            <span className="events-preview-card__status events-preview-card__status--live">
              {t("eventsStatusLive")}
            </span>
          ) : (
            <span className="events-preview-card__status events-preview-card__status--draft">
              {t("eventsStatusInactive")}
            </span>
          )}
        </div>
        <div className="events-preview-card__body">
          <h3 className="events-preview-card__title">
            {form.title.trim() || t("eventsFormHeroFallback")}
          </h3>
          <p className="events-preview-card__message">
            {form.message.trim() || t("eventsFieldDescriptionPlaceholder")}
          </p>
          {form.capacity !== "" && Number(form.capacity) > 0 ? (
            <p className="events-preview-card__capacity">
              <i className="fa-solid fa-users" aria-hidden />
              {t("eventsCapacityValue", { count: Number(form.capacity) })}
            </p>
          ) : null}
          {selectedMenus.length > 0 ? (
            <div className="events-preview-card__menus">
              <span className="events-preview-card__menus-label">{t("eventsFormPreviewMenus")}</span>
              <div className="events-preview-card__chips">
                {selectedMenus.map((menu) => (
                  <span key={menu._id} className="events-preview-card__chip">
                    {getMenuLabel(menu, lang)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {form.startDate && form.endDate ? (
            <div className="events-preview-card__dates">
              <i className="fa-regular fa-clock" />
              {formatDateRange(form.startDate, form.endDate)}
            </div>
          ) : null}
          {form.startDate && form.isActive ? (
            <div className="events-preview-card__publish">
              <i className="fa-regular fa-bell" />
              {(() => {
                const publishAt = getPublishDate(form.startDate, form.publishBeforeAmount, form.publishBeforeUnit);
                const unpublishAt = form.publishUntil || form.endDate;
                if (!publishAt) return null;
                if (unpublishAt) {
                  return t("eventsPublishWindowPreview", {
                    from: formatDateTime(publishAt),
                    until: formatDateTime(unpublishAt),
                  });
                }
                return t("eventsPublishDatePreview", { date: formatDateTime(publishAt) });
              })()}
            </div>
          ) : null}
          {form.isRecurring ? (
            <div className="events-preview-card__recurrence">
              <i className="fa-solid fa-rotate" />
              {formatRecurrenceLabel(form, t)}
            </div>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

function RequiredMark() {
  return (
    <span className="events-field__required" aria-hidden="true">
      *
    </span>
  );
}

function FieldError({ message, id }) {
  if (!message) return null;
  return (
    <p className="events-field__error" id={id} role="alert">
      {message}
    </p>
  );
}

function StudioSection({ icon, title, hint, children }) {
  return (
    <section className="events-studio-section">
      <header className="events-studio-section__head">
        <span className="events-studio-section__icon" aria-hidden>
          <i className={icon} />
        </span>
        <div className="events-studio-section__copy">
          <h3 className="events-studio-section__title">{title}</h3>
          {hint ? <p className="events-studio-section__hint">{hint}</p> : null}
        </div>
      </header>
      <div className="events-studio-section__body">{children}</div>
    </section>
  );
}

const getEventFormErrors = (form, t, maxClientPosition) => {
  const errors = {};

  if (!String(form.title || "").trim()) {
    errors.title = t("eventsValidationTitleRequired");
  }
  if (!String(form.message || "").trim()) {
    errors.message = t("eventsValidationDescriptionRequired");
  }
  if (!form.startDate) {
    errors.startDate = t("eventsValidationStartRequired");
  }
  if (!form.endDate) {
    errors.endDate = t("eventsValidationEndRequired");
  } else if (form.startDate) {
    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end < start) {
      errors.endDate = t("eventsValidationEndAfterStart");
    }
  }
  if (form.capacity !== "") {
    const parsedCapacity = Math.floor(Number(form.capacity));
    if (!Number.isFinite(parsedCapacity) || parsedCapacity < 1) {
      errors.capacity = t("eventsInvalidCapacityText");
    }
  }
  if (form.isRecurring && getRecurrenceType(form) === "custom") {
    const months = getRecurrenceMonths(form);
    const days = getRecurrenceMonthDaysList(form);
    if (!months.length || !days.length) {
      errors.recurrence = t("eventsMissingCustomRecurrenceText");
    }
  }
  if (form.publishUntil && form.startDate) {
    const until = new Date(form.publishUntil);
    const visibleFrom = getPublishDate(form.startDate, form.publishBeforeAmount, form.publishBeforeUnit);
    if (
      !Number.isNaN(until.getTime()) &&
      visibleFrom &&
      until.getTime() < visibleFrom.getTime()
    ) {
      errors.publishUntil = t("eventsValidationPublishUntilAfterVisible");
    }
  }
  if (!form.isSpotlight && String(form.clientPosition || "").trim() !== "") {
    const position = Number(form.clientPosition);
    const max = Math.max(1, Number(maxClientPosition) || 1);
    if (!Number.isInteger(position) || position < 1 || position > max) {
      errors.clientPosition = t("eventsValidationClientPosition", { max });
    }
  }

  return errors;
};

function EventVisibilityToggle({ isActive, onChange, t }) {
  return (
    <div className="events-visibility-control events-visibility-control--card">
      <div className="events-visibility-control__copy">
        <span className="events-visibility-control__title">
          <i className="fa-solid fa-eye" aria-hidden />
          {t("eventsFieldVisibility")}
        </span>
        <p className="events-visibility-control__hint">{t("eventsFieldActiveHint")}</p>
      </div>
      <div className="events-visibility-toggle" role="group" aria-label={t("eventsFieldVisibility")}>
        <button
          type="button"
          className={`events-visibility-toggle__btn events-visibility-toggle__btn--show${isActive ? " is-active" : ""}`}
          onClick={() => onChange(true)}
        >
          <i className="fa-solid fa-eye" aria-hidden />
          {t("eventsVisibilityShow")}
        </button>
        <button
          type="button"
          className={`events-visibility-toggle__btn events-visibility-toggle__btn--hide${!isActive ? " is-active" : ""}`}
          onClick={() => onChange(false)}
        >
          <i className="fa-solid fa-eye-slash" aria-hidden />
          {t("eventsVisibilityHide")}
        </button>
      </div>
    </div>
  );
}

const EVENT_STATUS_MENU_OPTIONS = [
  { id: "show", field: "isActive", value: true, icon: "fa-eye", labelKey: "eventsVisibilityShow" },
  { id: "hide", field: "isActive", value: false, icon: "fa-eye-slash", labelKey: "eventsVisibilityHide" },
  { id: "mark-almost-full", field: "availability", value: "almost_full", icon: "fa-triangle-exclamation", labelKey: "eventsStatusMarkAlmostFull" },
  { id: "mark-full", field: "availability", value: "full", icon: "fa-users-slash", labelKey: "eventsStatusMarkFull" },
  { id: "auto-capacity", field: "availability", value: "auto", icon: "fa-sliders", labelKey: "eventsStatusAutoCapacity" },
];

const isEventStatusOptionSelected = (event, option) => {
  if (option.field === "isActive") {
    return option.value ? event?.isActive !== false : event?.isActive === false;
  }
  if (option.field === "availability") {
    if (option.value === "full") return event?.isManuallyFull === true;
    if (option.value === "almost_full") return event?.isAlmostFull === true;
    return event?.isManuallyFull !== true && event?.isAlmostFull !== true;
  }
  return false;
};

const getEventStatusOptionLabel = (field, value, t) => {
  if (field === "isActive") {
    return value ? t("eventsVisibilityShow") : t("eventsVisibilityHide");
  }
  if (field === "availability") {
    if (value === "full") return t("eventsStatusMarkFull");
    if (value === "almost_full") return t("eventsStatusMarkAlmostFull");
    return t("eventsStatusAutoCapacity");
  }
  return "";
};

const buildEventStatusPayload = (field, value) => {
  if (field === "availability") {
    if (value === "full") {
      return { isManuallyFull: true, isAlmostFull: false };
    }
    if (value === "almost_full") {
      return { isManuallyFull: false, isAlmostFull: true };
    }
    return { isManuallyFull: false, isAlmostFull: false, manualRemainingPlaces: null };
  }
  return { [field]: value };
};

const resolveEventPlacesForDisplay = (event, changes = {}) => {
  const merged = { ...event, ...changes };
  const capacity =
    merged.capacity != null && Number(merged.capacity) > 0
      ? Math.floor(Number(merged.capacity))
      : null;
  const confirmedGuests = Math.max(0, Math.floor(Number(merged.confirmedGuests) || 0));
  const computedRemainingPlaces =
    capacity != null ? Math.max(0, capacity - confirmedGuests) : null;

  let manualRemainingPlaces = merged.manualRemainingPlaces;
  if (changes.manualRemainingPlaces !== undefined) {
    manualRemainingPlaces = changes.manualRemainingPlaces;
  } else if (merged.isManuallyFull !== true && merged.isAlmostFull !== true) {
    if (manualRemainingPlaces === 0) manualRemainingPlaces = null;
  }

  const isManualRemaining =
    manualRemainingPlaces != null && Number.isFinite(Number(manualRemainingPlaces));
  const remainingPlaces = isManualRemaining
    ? Math.max(0, Math.floor(Number(manualRemainingPlaces)))
    : computedRemainingPlaces;

  return {
    ...merged,
    manualRemainingPlaces: isManualRemaining ? manualRemainingPlaces : null,
    confirmedGuests,
    capacity,
    computedRemainingPlaces,
    isManualRemaining,
    remainingPlaces,
  };
};

function getEventStatusMeta(event, archived, t) {
  if (archived) {
    return { label: t("eventsStatusArchived"), className: "events-badge--archived", key: "archived" };
  }
  if (!event?.isActive) {
    return { label: t("eventsStatusInactive"), className: "events-badge--inactive", key: "hidden" };
  }
  if (isEventPendingPublish(event)) {
    if (isEventFull(event)) {
      return { label: t("eventsStatusFull"), className: "events-badge--full", key: "full" };
    }
    if (isEventAlmostFull(event)) {
      return { label: t("eventsStatusAlmostFull"), className: "events-badge--almost-full", key: "almost_full" };
    }
    return { label: t("eventsStatusPendingPublish"), className: "events-badge--pending", key: "pending" };
  }
  if (isEventLive(event)) {
    if (isEventFull(event)) {
      return { label: t("eventsStatusFull"), className: "events-badge--full", key: "full" };
    }
    if (isEventAlmostFull(event)) {
      return { label: t("eventsStatusAlmostFull"), className: "events-badge--almost-full", key: "almost_full" };
    }
    return { label: t("eventsStatusLive"), className: "events-badge--active events-badge--live", key: "live" };
  }
  if (isEventScheduled(event)) {
    if (isEventFull(event)) {
      return { label: t("eventsStatusFull"), className: "events-badge--full", key: "full" };
    }
    if (isEventAlmostFull(event)) {
      return { label: t("eventsStatusAlmostFull"), className: "events-badge--almost-full", key: "almost_full" };
    }
    return { label: t("eventsStatusScheduled"), className: "events-badge--announcement", key: "scheduled" };
  }
  return { label: t("eventsStatusEnded"), className: "events-badge--inactive", key: "ended" };
}

function EventStatusPicker({ event, archived, t, isOpen, isUpdating, onToggle, onStatusChange }) {
  const pickerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  const status = getEventStatusMeta(event, archived, t);

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 210;
    const menuHeight = EVENT_STATUS_MENU_OPTIONS.length * 40 + 28;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;
    const maxLeft = window.innerWidth - menuWidth - 12;
    const left = Math.min(Math.max(12, rect.left), maxLeft);

    setMenuPosition({
      top: openAbove ? rect.top - gap : rect.bottom + gap,
      left,
      width: Math.max(rect.width, menuWidth),
      transform: openAbove ? "translateY(-100%)" : "none",
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return undefined;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (clickEvent) => {
      const target = clickEvent.target;
      if (pickerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onToggle(false);
    };
    const handleEscape = (keydownEvent) => {
      if (keydownEvent.key === "Escape") onToggle(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onToggle]);

  if (archived) {
    return (
      <span className="events-badge events-badge--archived">
        <i className="fa-solid fa-box-archive events-badge__icon" aria-hidden />
        {status.label}
      </span>
    );
  }

  const menu =
    isOpen && menuPosition
      ? createPortal(
          <ul
            ref={menuRef}
            className="events-status-menu events-status-menu--portal"
            style={menuPosition}
            role="listbox"
            aria-label={t("eventsStatusMenuLabel")}
          >
            {EVENT_STATUS_MENU_OPTIONS.map((option, index) => (
              <Fragment key={option.id}>
                {index === 2 ? (
                  <li key={`${option.id}-divider`} className="events-status-menu__divider" role="separator" aria-hidden />
                ) : null}
                <li key={option.id} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isEventStatusOptionSelected(event, option)}
                    className={`events-status-menu__item events-status-menu__item--${option.id}${
                      isEventStatusOptionSelected(event, option) ? " is-active" : ""
                    }`}
                    onClick={() => {
                      onToggle(false);
                      if (!isEventStatusOptionSelected(event, option)) {
                        onStatusChange(event, option.field, option.value);
                      }
                    }}
                  >
                    <i className={`fa-solid ${option.icon} events-status-menu__icon`} aria-hidden />
                    {t(option.labelKey)}
                    {isEventStatusOptionSelected(event, option) ? (
                      <i className="fa-solid fa-check events-status-menu__check" aria-hidden />
                    ) : null}
                  </button>
                </li>
              </Fragment>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div className="events-status-picker" ref={pickerRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`events-badge events-status-picker__btn ${status.className}${
          isOpen ? " is-open" : ""
        }${isUpdating ? " is-updating" : ""}`}
        onClick={() => !isUpdating && onToggle(!isOpen)}
        disabled={isUpdating}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t("eventsStatusMenuLabel")}
        title={t("eventsStatusMenuLabel")}
      >
        {status.key === "archived" ? (
          <i className="fa-solid fa-box-archive events-badge__icon" aria-hidden />
        ) : (
          <span className="events-badge__dot" aria-hidden />
        )}
        <span className="events-status-picker__label">{status.label}</span>
        <i className="fa-solid fa-chevron-down events-status-picker__caret" aria-hidden />
      </button>
      {menu}
    </div>
  );
}

function EventDetailsModal({ open, event, archived, menuEntries, eventsList, onClose, onEdit, t }) {
  if (!open || !event) return null;

  const status = getEventStatusMeta(event, archived, t);
  const publishAt = getEventPublishDate(event);

  return (
    <div className="events-modal-backdrop events-modal-backdrop--studio" onClick={onClose} role="presentation">
      <div
        className="events-modal events-modal--studio events-modal--details"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="events-details-title"
      >
        <header className="events-studio-header">
          <div className="events-studio-header__icon">
            <i className="fa-regular fa-calendar-check" />
          </div>
          <div className="events-studio-header__text">
            <h2 className="events-studio-header__title" id="events-details-title">
              {t("eventsDetailsTitle")}
            </h2>
            <p className="events-studio-header__subtitle">{event.title}</p>
          </div>
          <button type="button" className="events-modal__close" onClick={onClose} aria-label={t("eventsCancel")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        <div className="events-details-body">
          <div className="events-details-hero">
            <div className="events-details-hero__media">
              {event.imageUrl ? (
                <UploadThumb
                  candidates={getEventImageCandidates(event.imageUrl)}
                  className="events-details-hero__image"
                  placeholderClassName="events-details-hero__image events-details-hero__image--placeholder"
                />
              ) : (
                <div className="events-details-hero__image events-details-hero__image--placeholder">
                  <i className="fa-regular fa-image" aria-hidden />
                </div>
              )}
            </div>
            <div className="events-details-hero__content">
              <div className="events-details-hero__badges">
                <span className={`events-badge ${status.className}`}>
                  <span className="events-badge__dot" />
                  {status.label}
                </span>
                {event.isSpotlight ? (
                  <span className="events-spotlight-badge">
                    <i className="fa-solid fa-star" aria-hidden />
                    {t("eventsSpotlightBadge")}
                  </span>
                ) : null}
                {event.isRecurring ? (
                  <span className="events-recurrence-badge">
                    <i className="fa-solid fa-rotate" aria-hidden />
                    {formatRecurrenceLabel(event, t)}
                  </span>
                ) : null}
              </div>
              <h3 className="events-details-hero__title">{event.title}</h3>
              <p className="events-details-hero__message">{event.message}</p>
            </div>
          </div>

          <div className="events-details-grid">
            <section className="events-details-card">
              <h4 className="events-details-card__title">
                <i className="fa-regular fa-calendar" aria-hidden />
                {t("eventsDetailsSchedule")}
              </h4>
              <dl className="events-details-list">
                <div className="events-details-list__row">
                  <dt>{t("eventsFieldStart")}</dt>
                  <dd>{formatDateTime(event.startDate)}</dd>
                </div>
                <div className="events-details-list__row">
                  <dt>{t("eventsFieldEnd")}</dt>
                  <dd>{formatDateTime(event.endDate)}</dd>
                </div>
                <div className="events-details-list__row">
                  <dt>{t("eventsFieldVisibility")}</dt>
                  <dd>{event.isActive ? t("eventsVisibilityShow") : t("eventsVisibilityHide")}</dd>
                </div>
                {!event.isSpotlight ? (
                  <div className="events-details-list__row">
                    <dt>{t("eventsFieldClientPosition")}</dt>
                    <dd>
                      {getClientPositionForEvent(event, eventsList || []) || t("eventsFieldClientPositionPlaceholder")}
                    </dd>
                  </div>
                ) : null}
                {event.isActive && publishAt ? (
                  <div className="events-details-list__row">
                    <dt>{t("eventsFieldPublishDate")}</dt>
                    <dd>{formatDateTime(publishAt)}</dd>
                  </div>
                ) : null}
                {getEventUnpublishDate(event) ? (
                  <div className="events-details-list__row">
                    <dt>{t("eventsFieldPublishUntil")}</dt>
                    <dd>{formatDateTime(getEventUnpublishDate(event))}</dd>
                  </div>
                ) : null}
                {event.isRecurring ? (
                  <div className="events-details-list__row">
                    <dt>{t("eventsDetailsOccurrences")}</dt>
                    <dd>{t("eventsOccurrenceCount", { count: Number(event.occurrenceCount) || 1 })}</dd>
                  </div>
                ) : null}
                <div className="events-details-list__row">
                  <dt>{t("eventsFieldCapacity")}</dt>
                  <dd>
                    {event.capacity != null && Number(event.capacity) > 0
                      ? t("eventsCapacityValue", { count: Number(event.capacity) })
                      : t("eventsCapacityUnlimited")}
                  </dd>
                </div>
                <div className="events-details-list__row">
                  <dt>{t("eventsFieldAvailability")}</dt>
                  <dd>
                    {event.isManuallyFull
                      ? t("eventsStatusMarkFull")
                      : event.isAlmostFull
                        ? t("eventsStatusMarkAlmostFull")
                        : isEventFull(event)
                          ? t("eventsStatusFull")
                          : t("eventsStatusAutoCapacity")}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="events-details-card">
              <h4 className="events-details-card__title">
                <i className="fa-solid fa-utensils" aria-hidden />
                {t("eventsColMenus")}
              </h4>
              {menuEntries.length === 0 ? (
                <p className="events-details-empty">—</p>
              ) : (
                <div className="events-menu-chips">
                  {menuEntries.map((menu) => (
                    <Link key={menu.id} to={`/edit-menu/${menu.id}`} className="events-menu-chip">
                      <i className="fa-solid fa-utensils" aria-hidden />
                      <span>{menu.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {!archived ? (
              <section className="events-details-card">
                <h4 className="events-details-card__title">
                  <i className="fa-solid fa-calendar-check" aria-hidden />
                  {t("eventsColReservations")}
                </h4>
                <Link to={`/events/${event._id}/reservations`} className="events-reservations-btn">
                  <i className="fa-solid fa-calendar-check" aria-hidden />
                  <span>{t("eventsReservationsBtn")}</span>
                  {Number(event.reservationCount) > 0 ? (
                    <span className="events-reservations-btn__count">{event.reservationCount}</span>
                  ) : null}
                </Link>
              </section>
            ) : null}
          </div>
        </div>

        <footer className="events-modal__footer events-modal__footer--studio">
          <button type="button" className="events-btn events-btn--ghost" onClick={onClose}>
            {t("eventsDetailsClose")}
          </button>
          {!archived ? (
            <button
              type="button"
              className="events-btn events-btn--edit events-btn--glow"
              onClick={() => onEdit(event)}
            >
              <i className="fa-solid fa-pen" />
              {t("eventsActionEdit")}
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  );
}

function EventShareModal({ open, event, onClose, t }) {
  const [copyFeedback, setCopyFeedback] = useState("");

  if (!open || !event) return null;

  const imageUrl = resolveEventImage(event.imageUrl);
  const shareUrl = buildEventShareUrl(event._id);
  const shareText = [event.title, event.message].filter(Boolean).join("\n\n");
  const fullShareText = shareUrl ? `${shareText}\n\n${shareUrl}` : shareText;

  const copyValue = async (value, feedbackKey) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback(feedbackKey);
      setTimeout(() => setCopyFeedback(""), 2200);
    } catch {
      Swal.fire(t("eventsError"), t("eventsShareCopyFailed"), "error");
    }
  };

  const openShareWindow = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer,width=640,height=520");
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      Swal.fire(t("eventsShareUnavailable"), t("eventsShareUnavailableText"), "info");
      return;
    }
    try {
      const payload = {
        title: event.title || t("eventsShareTitle"),
        text: shareText,
      };
      if (shareUrl) payload.url = shareUrl;

      if (imageUrl && navigator.canShare) {
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const extension = blob.type?.includes("png") ? "png" : "jpg";
          const file = new File([blob], `event.${extension}`, { type: blob.type || "image/jpeg" });
          if (navigator.canShare({ ...payload, files: [file] })) {
            payload.files = [file];
          }
        } catch {
          // Image attachment is optional; continue with text + URL.
        }
      }

      await navigator.share(payload);
    } catch (error) {
      if (error?.name !== "AbortError") {
        Swal.fire(t("eventsError"), t("eventsShareFailed"), "error");
      }
    }
  };

  const shareOptions = [
    {
      key: "native",
      label: t("eventsShareNative"),
      icon: "fa-solid fa-share-nodes",
      className: "events-share-option--native",
      onClick: handleNativeShare,
      hidden: typeof navigator === "undefined" || !navigator.share,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: "fa-brands fa-facebook-f",
      className: "events-share-option--facebook",
      onClick: () => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`),
      disabled: !shareUrl,
    },
    {
      key: "twitter",
      label: "X",
      icon: "fa-brands fa-x-twitter",
      className: "events-share-option--twitter",
      onClick: () =>
        openShareWindow(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
        ),
      disabled: !shareUrl,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: "fa-brands fa-whatsapp",
      className: "events-share-option--whatsapp",
      onClick: () => openShareWindow(`https://web.whatsapp.com/send?text=${encodeURIComponent(shareUrl || fullShareText)}`),
      disabled: !shareUrl && !fullShareText,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: "fa-brands fa-linkedin-in",
      className: "events-share-option--linkedin",
      onClick: () =>
        openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`),
      disabled: !shareUrl,
    },
  ].filter((option) => !option.hidden);

  return (
    <div className="events-modal-backdrop events-modal-backdrop--studio" onClick={onClose} role="presentation">
      <div
        className="events-modal events-modal--studio events-modal--share"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="events-share-title"
      >
        <header className="events-studio-header">
          <div className="events-studio-header__icon events-studio-header__icon--share">
            <i className="fa-solid fa-share-nodes" />
          </div>
          <div className="events-studio-header__text">
            <h2 className="events-studio-header__title" id="events-share-title">
              {t("eventsShareTitle")}
            </h2>
            <p className="events-studio-header__subtitle">{t("eventsShareSubtitle")}</p>
          </div>
          <button type="button" className="events-modal__close" onClick={onClose} aria-label={t("eventsCancel")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        <div className="events-share-body">
          <div className="events-share-preview">
            <div className="events-share-preview__media">
              {event.imageUrl ? (
                <UploadThumb
                  candidates={getEventImageCandidates(event.imageUrl)}
                  className="events-share-preview__image"
                  placeholderClassName="events-share-preview__image events-share-preview__image--placeholder"
                />
              ) : (
                <div className="events-share-preview__image events-share-preview__image--placeholder">
                  <i className="fa-regular fa-image" aria-hidden />
                </div>
              )}
            </div>
            <div className="events-share-preview__content">
              <h3 className="events-share-preview__title">{event.title}</h3>
              <p className="events-share-preview__message">{event.message}</p>
              {shareUrl ? (
                <p className="events-share-preview__url">{shareUrl}</p>
              ) : (
                <p className="events-share-preview__url events-share-preview__url--missing">{t("eventsShareNoUrl")}</p>
              )}
              <p className="events-share-preview__hint">{t("eventsSharePreviewHint")}</p>
            </div>
          </div>

          <div className="events-share-options" role="list">
            {shareOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`events-share-option ${option.className}`}
                onClick={option.onClick}
                disabled={option.disabled}
                role="listitem"
              >
                <span className="events-share-option__icon" aria-hidden>
                  <i className={option.icon} />
                </span>
                <span>{option.label}</span>
              </button>
            ))}
          </div>

          <div className="events-share-copy">
            <button
              type="button"
              className="events-share-copy__btn"
              onClick={() => copyValue(shareUrl, "link")}
              disabled={!shareUrl}
            >
              <i className="fa-solid fa-link" aria-hidden />
              {copyFeedback === "link" ? t("eventsShareCopied") : t("eventsShareCopyLink")}
            </button>
            <button type="button" className="events-share-copy__btn" onClick={() => copyValue(fullShareText, "text")}>
              <i className="fa-solid fa-align-left" aria-hidden />
              {copyFeedback === "text" ? t("eventsShareCopied") : t("eventsShareCopyText")}
            </button>
            {imageUrl ? (
              <button type="button" className="events-share-copy__btn" onClick={() => copyValue(imageUrl, "image")}>
                <i className="fa-regular fa-image" aria-hidden />
                {copyFeedback === "image" ? t("eventsShareCopied") : t("eventsShareCopyImage")}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function EventFormModal({ open, editingEvent, initialForm, menus, maxClientPosition, onClose, onSaved, t, i18n }) {
  const [form, setForm] = useState(initialForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [showAllErrors, setShowAllErrors] = useState(false);
  const openedSessionRef = useRef(null);
  const scheduleSectionRef = useRef(null);

  const markTouched = (field) => {
    setTouchedFields((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  };

  const markScheduleFieldsTouched = () => {
    markTouched("startDate");
    markTouched("endDate");
  };

  const handleScheduleFocusOut = (event) => {
    const next = event.relatedTarget;
    if (!scheduleSectionRef.current?.contains(next)) {
      markScheduleFieldsTouched();
    }
  };

  const handleStartBlur = (event) => {
    markTouched("startDate");
    const next = event.relatedTarget;
    if (!scheduleSectionRef.current?.contains(next)) {
      markTouched("endDate");
    }
  };

  useEffect(() => {
    if (!open) {
      openedSessionRef.current = null;
      return;
    }

    const sessionId = editingEvent?._id || "create";
    if (openedSessionRef.current === sessionId) return;
    openedSessionRef.current = sessionId;

    setForm(initialForm);
    setImageFile(null);
    setRemoveImage(false);
    setImagePreview(editingEvent?.imageUrl ? resolveEventImage(editingEvent.imageUrl) : "");
    setSaving(false);
    setImageProcessing(false);
    setTouchedFields({});
    setShowAllErrors(false);
  }, [open, editingEvent?._id, editingEvent?.imageUrl, initialForm]);

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const toggleMenu = (menuId) => {
    const id = String(menuId);
    setForm((prev) => {
      const selected = new Set((prev.menuFKs || []).map(String));
      if (selected.has(id)) selected.delete(id);
      else selected.add(id);
      return { ...prev, menuFKs: Array.from(selected) };
    });
  };

  const selectAllMenus = () => {
    setForm((prev) => ({
      ...prev,
      menuFKs: menus.map((menu) => String(menu._id)),
    }));
  };

  const removeAllMenus = () => {
    setForm((prev) => ({ ...prev, menuFKs: [] }));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const { isValid, message } = validateImageFile(file, 5);
    if (!isValid) {
      Swal.fire(t("eventsError"), message || t("eventsErrorImage"), "warning");
      return;
    }

    setImageFile(file);
    setRemoveImage(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    setRemoveImage(true);
  };

  const applyDuration = (days) => {
    setForm((prev) => {
      const next = durationPresets(days, prev.startDate);
      return {
        ...prev,
        ...next,
        publishUntil: prev.publishUntil || next.endDate,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = getEventFormErrors(form, t, maxClientPosition);
    if (Object.keys(validationErrors).length > 0) {
      setShowAllErrors(true);
      return;
    }

    const payload = {
      title: form.title.trim(),
      message: form.message.trim(),
      capacity: form.capacity === "" ? null : Math.floor(Number(form.capacity)),
      menuFKs: form.menuFKs,
      clientPosition: form.isSpotlight
        ? null
        : String(form.clientPosition || "").trim() === ""
          ? null
          : Math.floor(Number(form.clientPosition)),
      startDate: new Date(form.startDate).toISOString(),
      endDate: new Date(form.endDate).toISOString(),
      publishBeforeAmount: Math.max(0, Math.floor(Number(form.publishBeforeAmount) || 0)),
      publishBeforeUnit: normalizePublishBeforeUnit(form.publishBeforeUnit),
      publishUntil: new Date(form.publishUntil || form.endDate).toISOString(),
      isRecurring: Boolean(form.isRecurring),
      recurrenceType: form.isRecurring ? getRecurrenceType(form) : "none",
      recurrenceWeekday: getRecurrenceWeekday(form),
      recurrenceMonthDay: getRecurrenceMonthDay(form),
      recurrenceMonths: getRecurrenceMonths(form),
      recurrenceMonthDays: getRecurrenceMonthDaysList(form),
      isSpotlight: Boolean(form.isSpotlight),
      isActive: form.isActive,
      removeImage,
    };

    setSaving(true);
    try {
      const result = await saveEvent({ payload, file: imageFile, id: editingEvent?._id });
      Swal.fire(
        editingEvent ? t("eventsUpdated") : t("eventsCreated"),
        editingEvent ? t("eventsUpdatedText") : t("eventsCreatedText"),
        "success"
      );
      onSaved(result?.data);
      onClose();
    } catch (error) {
      Swal.fire(
        t("eventsError"),
        error?.response?.data?.message || error.message || t("eventsErrorSave"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const formErrors = getEventFormErrors(form, t, maxClientPosition);
  const visibleErrors = Object.fromEntries(
    Object.entries(formErrors).filter(([field]) => showAllErrors || touchedFields[field])
  );
  const hasVisibleErrors = Object.keys(visibleErrors).length > 0;

  if (!open) return null;

  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const selectedMenuCount = form.menuFKs?.length || 0;
  const allMenusSelected = menus.length > 0 && selectedMenuCount === menus.length;
  const publishPreviewDate = form.startDate
    ? getPublishDate(form.startDate, form.publishBeforeAmount, form.publishBeforeUnit)
    : null;

  const applyPublishPreset = (amount, unit) => {
    setForm((prev) => ({ ...prev, publishBeforeAmount: amount, publishBeforeUnit: unit }));
  };

  const isPublishPresetActive = (amount, unit) =>
    Number(form.publishBeforeAmount) === amount && form.publishBeforeUnit === unit;

  return (
    <div className="events-modal-backdrop events-modal-backdrop--studio" onClick={onClose} role="presentation">
      <div
        className="events-modal events-modal--studio"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="events-modal-title"
      >
        <header className="events-studio-header">
          <div className="events-studio-header__icon">
            <i className="fa-regular fa-calendar-plus" />
          </div>
          <div className="events-studio-header__text">
            <h2 className="events-studio-header__title" id="events-modal-title">
              {editingEvent ? t("eventsEditModalTitle") : t("eventsCreateModalTitle")}
            </h2>
            <p className="events-studio-header__subtitle">{t("eventsFormSubtitle")}</p>
          </div>
          <button type="button" className="events-modal__close" onClick={onClose} aria-label={t("eventsCancel")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="events-modal__form-wrap events-modal__form-wrap--studio" noValidate>
          <div className="events-studio-layout">
            <div className="events-studio-form">
              {showAllErrors && hasVisibleErrors ? (
                <div className="events-form-validation" role="alert">
                  <i className="fa-solid fa-circle-exclamation" aria-hidden />
                  <span>{t("eventsFormValidationSummary")}</span>
                </div>
              ) : null}

              <StudioSection
                icon="fa-solid fa-pen-nib"
                title={t("eventsFormSectionBasics")}
                hint={t("eventsFormSectionBasicsHint")}
              >
                <div className="events-studio-hero">
                  <div className="events-studio-block events-studio-block--cover">
                    <label className="events-studio-block__label">
                      <i className="fa-regular fa-image" />
                      {t("eventsFieldImage")}
                    </label>
                    <div className="events-cover-upload">
                      {imagePreview ? (
                        <div className="events-cover-upload__filled">
                          {imageFile ? (
                            <img src={imagePreview} alt="" />
                          ) : (
                            <UploadThumb
                              candidates={getEventImageCandidates(editingEvent?.imageUrl)}
                              className="events-cover-upload__preview"
                              placeholderClassName="events-cover-upload__preview events-cover-upload__preview--placeholder"
                            />
                          )}
                          <div className="events-cover-upload__actions">
                            <label className="events-cover-upload__btn">
                              <i className="fa-solid fa-arrows-rotate" />
                              {t("eventsChangeImage")}
                              <input type="file" accept={IMAGE_PICKER_ACCEPT} hidden onChange={handleImageChange} disabled={imageProcessing} />
                            </label>
                            <button type="button" className="events-cover-upload__btn events-cover-upload__btn--danger" onClick={handleRemoveImage}>
                              <i className="fa-solid fa-trash" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className={`events-cover-upload__empty${imageProcessing ? " is-processing" : ""}`}>
                          <i className={`fa-solid ${imageProcessing ? "fa-spinner fa-spin" : "fa-cloud-arrow-up"}`} />
                          <span>{imageProcessing ? t("eventsProcessingImage") : t("eventsUploadImage")}</span>
                          <small>{t("eventsFieldImageHint")}</small>
                          <input type="file" accept={IMAGE_PICKER_ACCEPT} hidden onChange={handleImageChange} disabled={imageProcessing} />
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="events-studio-hero__copy">
                    <div className="events-studio-block">
                      <label className="events-studio-block__label" htmlFor="event-title">
                        <i className="fa-solid fa-heading" />
                        {t("eventsFieldTitle")}
                        <RequiredMark />
                      </label>
                      <input
                        id="event-title"
                        className={`events-studio-input events-studio-input--title${visibleErrors.title ? " is-invalid" : ""}`}
                        value={form.title}
                        onChange={(e) => updateForm("title", e.target.value)}
                        onBlur={() => markTouched("title")}
                        placeholder={t("eventsFieldTitlePlaceholder")}
                        aria-invalid={Boolean(visibleErrors.title)}
                        aria-describedby={visibleErrors.title ? "event-title-error" : undefined}
                        required
                      />
                      <FieldError message={visibleErrors.title} id="event-title-error" />
                    </div>

                    <div className="events-studio-block events-studio-block--flush">
                      <label className="events-studio-block__label" htmlFor="event-message">
                        <i className="fa-regular fa-file-lines" />
                        {t("eventsFieldDescription")}
                        <RequiredMark />
                      </label>
                      <textarea
                        id="event-message"
                        className={`events-studio-input events-studio-input--area${visibleErrors.message ? " is-invalid" : ""}`}
                        value={form.message}
                        onChange={(e) => updateForm("message", e.target.value)}
                        onBlur={() => markTouched("message")}
                        placeholder={t("eventsFieldDescriptionPlaceholder")}
                        rows={4}
                        aria-invalid={Boolean(visibleErrors.message)}
                        aria-describedby={visibleErrors.message ? "event-message-error" : undefined}
                        required
                      />
                      <FieldError message={visibleErrors.message} id="event-message-error" />
                    </div>
                  </div>
                </div>

                <div className="events-studio-grid">
                  <div className="events-studio-block events-studio-block--flush">
                    <label className="events-studio-block__label" htmlFor="event-capacity">
                      <i className="fa-solid fa-users" />
                      {t("eventsFieldCapacity")}
                    </label>
                    <input
                      id="event-capacity"
                      type="number"
                      min={1}
                      step={1}
                      inputMode="numeric"
                      className={`events-studio-input${visibleErrors.capacity ? " is-invalid" : ""}`}
                      value={form.capacity}
                      onChange={(e) => updateForm("capacity", e.target.value)}
                      onBlur={() => markTouched("capacity")}
                      placeholder={t("eventsFieldCapacityPlaceholder")}
                      aria-invalid={Boolean(visibleErrors.capacity)}
                      aria-describedby={visibleErrors.capacity ? "event-capacity-error" : undefined}
                    />
                    <FieldError message={visibleErrors.capacity} id="event-capacity-error" />
                    <p className="events-field__hint">{t("eventsFieldCapacityHint")}</p>
                  </div>

                  {!form.isSpotlight ? (
                    <div className="events-studio-block events-studio-block--flush">
                      <label className="events-studio-block__label" htmlFor="event-client-position">
                        <i className="fa-solid fa-arrow-down-wide-short" />
                        {t("eventsFieldClientPosition")}
                      </label>
                      <input
                        id="event-client-position"
                        type="number"
                        min={1}
                        max={maxClientPosition}
                        step={1}
                        inputMode="numeric"
                        className={`events-studio-input${visibleErrors.clientPosition ? " is-invalid" : ""}`}
                        value={form.clientPosition}
                        onChange={(e) => updateForm("clientPosition", e.target.value)}
                        onBlur={() => markTouched("clientPosition")}
                        placeholder={t("eventsFieldClientPositionPlaceholder")}
                        aria-invalid={Boolean(visibleErrors.clientPosition)}
                        aria-describedby={visibleErrors.clientPosition ? "event-client-position-error" : undefined}
                      />
                      <p className="events-field__hint">
                        {t("eventsFieldClientPositionHint", { max: maxClientPosition })}
                      </p>
                      <FieldError message={visibleErrors.clientPosition} id="event-client-position-error" />
                    </div>
                  ) : null}
                </div>
              </StudioSection>

              <StudioSection
                icon="fa-solid fa-utensils"
                title={t("eventsFormSectionMenus")}
                hint={t("eventsFieldMenusHint")}
              >
              <div className="events-studio-block events-studio-block--flush">
                {menus.length > 0 ? (
                  <div className="events-studio-block__row events-studio-block__row--end">
                    <div className="events-menu-toolbar">
                      {selectedMenuCount > 0 ? (
                        <span className="events-studio-count">{t("eventsMenusSelected", { count: selectedMenuCount })}</span>
                      ) : null}
                      <div className="events-menu-toolbar__actions">
                        <button
                          type="button"
                          className="events-menu-toolbar__btn"
                          onClick={selectAllMenus}
                          disabled={allMenusSelected}
                        >
                          {t("eventsMenusSelectAll")}
                        </button>
                        <button
                          type="button"
                          className="events-menu-toolbar__btn events-menu-toolbar__btn--clear"
                          onClick={removeAllMenus}
                          disabled={selectedMenuCount === 0}
                        >
                          {t("eventsMenusRemoveAll")}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
                {menus.length === 0 ? (
                  <div className="events-menu-empty">
                    <i className="fa-solid fa-utensils" />
                    {t("eventsNoMenus")}
                  </div>
                ) : (
                  <div className="events-menu-grid events-menu-grid--studio">
                    {menus.map((menu) => {
                      const selected = (form.menuFKs || []).map(String).includes(String(menu._id));
                      return (
                        <button
                          key={menu._id}
                          type="button"
                          className={`events-menu-tile${selected ? " is-selected" : ""}`}
                          onClick={() => toggleMenu(menu._id)}
                        >
                          <span className="events-menu-tile__check">
                            <i className={`fa-solid ${selected ? "fa-check" : ""}`} />
                          </span>
                          <MenuThumb photo={menu.photo} />
                          <span className="events-menu-tile__label">{getMenuLabel(menu, lang)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              </StudioSection>

              <StudioSection
                icon="fa-regular fa-calendar"
                title={t("eventsFormSectionSchedule")}
                hint={t("eventsFormSectionScheduleHint")}
              >
                <div
                  ref={scheduleSectionRef}
                  className="events-schedule-section"
                  onBlur={handleScheduleFocusOut}
                >
                <div className="events-duration-presets events-duration-presets--studio">
                  {[
                    { days: 0, key: "eventsDurationToday" },
                    { days: 7, key: "eventsDuration1Week" },
                    { days: 30, key: "eventsDuration1Month" },
                  ].map(({ days, key }) => (
                    <button key={key} type="button" className="events-duration-btn" onClick={() => applyDuration(days)}>
                      {t(key)}
                    </button>
                  ))}
                </div>
                <div className="events-schedule-row events-schedule-row--studio">
                  <div className="events-field events-field--icon">
                    <label htmlFor="event-start">
                      {t("eventsFieldStart")}
                      <RequiredMark />
                    </label>
                    <div className="events-input-icon-wrap">
                      <i className="fa-regular fa-calendar" />
                      <input
                        id="event-start"
                        type="datetime-local"
                        className={visibleErrors.startDate ? "is-invalid" : ""}
                        value={form.startDate}
                        onChange={(e) => {
                          const nextStart = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            startDate: nextStart,
                            recurrenceWeekday: prev.isRecurring
                              ? getWeekdayFromDate(nextStart)
                              : prev.recurrenceWeekday,
                          }));
                        }}
                        onBlur={handleStartBlur}
                        aria-invalid={Boolean(visibleErrors.startDate)}
                        aria-describedby={visibleErrors.startDate ? "event-start-error" : undefined}
                        required
                      />
                    </div>
                    <FieldError message={visibleErrors.startDate} id="event-start-error" />
                  </div>
                  <div className="events-field events-field--icon">
                    <label htmlFor="event-end">
                      {t("eventsFieldEnd")}
                      <RequiredMark />
                    </label>
                    <div className="events-input-icon-wrap">
                      <i className="fa-regular fa-calendar-check" />
                      <input
                        id="event-end"
                        type="datetime-local"
                        className={visibleErrors.endDate ? "is-invalid" : ""}
                        value={form.endDate}
                        onChange={(e) => {
                          const nextEnd = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            endDate: nextEnd,
                            publishUntil: prev.publishUntil || nextEnd,
                          }));
                        }}
                        onBlur={() => markTouched("endDate")}
                        aria-invalid={Boolean(visibleErrors.endDate)}
                        aria-describedby={visibleErrors.endDate ? "event-end-error" : undefined}
                        required
                      />
                    </div>
                    <FieldError message={visibleErrors.endDate} id="event-end-error" />
                  </div>
                </div>
                </div>
                <div className="events-publish-timing">
                  <label className="events-studio-block__label" htmlFor="event-publish-amount">
                    <i className="fa-regular fa-bell" />
                    {t("eventsFieldPublishBefore")}
                  </label>
                  <p className="events-publish-timing__hint">{t("eventsFieldPublishBeforeHint")}</p>
                  <div className="events-publish-before-row">
                    <span className="events-publish-before-row__prefix">{t("eventsFieldPublishBeforePrefix")}</span>
                    <input
                      id="event-publish-amount"
                      type="number"
                      className="events-studio-input events-publish-before-row__amount"
                      min={0}
                      max={365}
                      value={form.publishBeforeAmount}
                      onChange={(e) => updateForm("publishBeforeAmount", Math.max(0, Number(e.target.value) || 0))}
                    />
                    <select
                      className="events-studio-input events-publish-before-row__unit"
                      value={form.publishBeforeUnit}
                      onChange={(e) => updateForm("publishBeforeUnit", e.target.value)}
                    >
                      <option value="days">{t("eventsPublishUnitDays")}</option>
                      <option value="weeks">{t("eventsPublishUnitWeeks")}</option>
                      <option value="months">{t("eventsPublishUnitMonths")}</option>
                    </select>
                    <span className="events-publish-before-row__suffix">{t("eventsFieldPublishBeforeSuffix")}</span>
                  </div>
                  <div className="events-duration-presets events-duration-presets--studio events-publish-presets">
                    {PUBLISH_BEFORE_PRESETS.map(({ amount, unit, key }) => (
                      <button
                        key={key}
                        type="button"
                        className={`events-duration-btn${isPublishPresetActive(amount, unit) ? " is-active" : ""}`}
                        onClick={() => applyPublishPreset(amount, unit)}
                      >
                        {t(key)}
                      </button>
                    ))}
                  </div>
                  <div className="events-field events-field--icon events-publish-until-field">
                    <label htmlFor="event-publish-until">{t("eventsFieldPublishUntil")}</label>
                    <p className="events-publish-timing__hint">{t("eventsFieldPublishUntilHint")}</p>
                    <div className="events-input-icon-wrap">
                      <i className="fa-regular fa-calendar-xmark" />
                      <input
                        id="event-publish-until"
                        type="datetime-local"
                        className={visibleErrors.publishUntil ? "is-invalid" : ""}
                        value={form.publishUntil || form.endDate || ""}
                        onChange={(e) => updateForm("publishUntil", e.target.value)}
                        onBlur={() => markTouched("publishUntil")}
                        aria-invalid={Boolean(visibleErrors.publishUntil)}
                        aria-describedby={visibleErrors.publishUntil ? "event-publish-until-error" : undefined}
                      />
                    </div>
                    <FieldError message={visibleErrors.publishUntil} id="event-publish-until-error" />
                  </div>
                </div>
              </StudioSection>

              <StudioSection
                icon="fa-solid fa-sliders"
                title={t("eventsFormSectionOptions")}
                hint={t("eventsFormSectionOptionsHint")}
              >
                <div className="events-studio-options">
                <div className="events-recurrence-block">
                  <label className="events-publish-inline events-publish-inline--recurrence">
                    <input
                      type="checkbox"
                      checked={form.isRecurring}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => ({
                          ...prev,
                          isRecurring: checked,
                          recurrenceType: checked ? prev.recurrenceType || "weekly" : prev.recurrenceType,
                          recurrenceWeekday: checked
                            ? getRecurrenceWeekday(prev)
                            : prev.recurrenceWeekday,
                          recurrenceMonthDay: checked
                            ? getRecurrenceMonthDay(prev)
                            : prev.recurrenceMonthDay,
                          recurrenceMonths: checked ? getRecurrenceMonths(prev) : prev.recurrenceMonths,
                          recurrenceMonthDays: checked ? getRecurrenceMonthDaysList(prev) : prev.recurrenceMonthDays,
                        }));
                      }}
                    />
                    <span className="events-publish-inline__box">
                      <i className="fa-solid fa-check" />
                    </span>
                    <span className="events-publish-inline__text">
                      <strong>{t("eventsFieldRecurring")}</strong>
                      <small>{t("eventsFieldRecurringHint")}</small>
                    </span>
                  </label>
                  {form.isRecurring ? (
                    <div className="events-recurrence-panel">
                      <div className="events-recurrence-type">
                        <span className="events-recurrence-weekdays__label">{t("eventsFieldRecurrenceType")}</span>
                        <div className="events-recurrence-type__chips">
                          <button
                            type="button"
                            className={`events-recurrence-type-btn${getRecurrenceType(form) === "weekly" ? " is-active" : ""}`}
                            onClick={() => updateForm("recurrenceType", "weekly")}
                          >
                            <i className="fa-solid fa-calendar-week" aria-hidden />
                            {t("eventsRecurrenceTypeWeekly")}
                          </button>
                          <button
                            type="button"
                            className={`events-recurrence-type-btn${getRecurrenceType(form) === "daily" ? " is-active" : ""}`}
                            onClick={() => updateForm("recurrenceType", "daily")}
                          >
                            <i className="fa-solid fa-sun" aria-hidden />
                            {t("eventsRecurrenceTypeDaily")}
                          </button>
                          <button
                            type="button"
                            className={`events-recurrence-type-btn${getRecurrenceType(form) === "custom" ? " is-active" : ""}`}
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                ...getRecurrenceFormFields({ ...prev, recurrenceType: "custom" }),
                              }))
                            }
                          >
                            <i className="fa-solid fa-calendar-plus" aria-hidden />
                            {t("eventsRecurrenceTypeCustom")}
                          </button>
                        </div>
                      </div>
                      {getRecurrenceType(form) === "daily" ? (
                        <p className="events-recurrence-daily-hint">{t("eventsRecurrenceDailyHint")}</p>
                      ) : getRecurrenceType(form) === "custom" ? (
                        <div className="events-recurrence-custom">
                          <div className="events-recurrence-months">
                            <span className="events-recurrence-weekdays__label">{t("eventsFieldRecurrenceMonths")}</span>
                            <p className="events-recurrence-monthdays__hint">{t("eventsRecurrenceCustomMonthsHint")}</p>
                            <div className="events-recurrence-months__chips">
                              {MONTH_OPTIONS.map(({ value, key }) => {
                                const selected = getRecurrenceMonths(form).includes(value);
                                return (
                                  <button
                                    key={value}
                                    type="button"
                                    className={`events-recurrence-month${selected ? " is-active" : ""}`}
                                    onClick={() =>
                                      setForm((prev) => ({
                                        ...prev,
                                        recurrenceMonths: toggleRecurrenceValue(getRecurrenceMonths(prev), value),
                                      }))
                                    }
                                  >
                                    {t(key)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="events-recurrence-monthdays">
                            <span className="events-recurrence-weekdays__label">{t("eventsFieldRecurrenceMonthDays")}</span>
                            <p className="events-recurrence-monthdays__hint">{t("eventsRecurrenceCustomDaysHint")}</p>
                            <div className="events-recurrence-monthdays__chips">
                              {MONTH_DAY_OPTIONS.map((day) => {
                                const selected = getRecurrenceMonthDaysList(form).includes(day);
                                return (
                                  <button
                                    key={day}
                                    type="button"
                                    className={`events-recurrence-monthday${selected ? " is-active" : ""}`}
                                    onClick={() =>
                                      setForm((prev) => ({
                                        ...prev,
                                        recurrenceMonthDays: toggleRecurrenceValue(getRecurrenceMonthDaysList(prev), day),
                                      }))
                                    }
                                  >
                                    {day}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <FieldError message={visibleErrors.recurrence} id="event-recurrence-error" />
                        </div>
                      ) : (
                        <div className="events-recurrence-weekdays">
                          <span className="events-recurrence-weekdays__label">{t("eventsFieldRecurrenceDay")}</span>
                          <div className="events-recurrence-weekdays__chips">
                            {WEEKDAY_OPTIONS.map(({ value, key }) => (
                              <button
                                key={value}
                                type="button"
                                className={`events-recurrence-day${getRecurrenceWeekday(form) === value ? " is-active" : ""}`}
                                onClick={() => updateForm("recurrenceWeekday", value)}
                              >
                                {t(key)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
                <label className="events-publish-inline events-publish-inline--spotlight">
                  <input
                    type="checkbox"
                    checked={form.isSpotlight}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => ({
                        ...prev,
                        isSpotlight: checked,
                        clientPosition: checked ? "" : prev.clientPosition,
                      }));
                    }}
                  />
                  <span className="events-publish-inline__box">
                    <i className="fa-solid fa-check" />
                  </span>
                  <span className="events-publish-inline__text">
                    <strong>{t("eventsFieldSpotlight")}</strong>
                    <small>{t("eventsFieldSpotlightHint")}</small>
                  </span>
                </label>
                <EventVisibilityToggle
                  isActive={form.isActive}
                  onChange={(value) => updateForm("isActive", value)}
                  t={t}
                />
                </div>
              </StudioSection>
            </div>

            <EventLivePreview
              form={form}
              imagePreview={imagePreview}
              storedImageUrl={removeImage ? "" : editingEvent?.imageUrl || ""}
              menus={menus}
              lang={lang}
              t={t}
            />
          </div>

          <footer className="events-modal__footer events-modal__footer--studio">
            <button type="button" className="events-btn events-btn--ghost" onClick={onClose} disabled={saving}>
              {t("eventsCancel")}
            </button>
            <button type="submit" className="events-btn events-btn--success events-btn--glow" disabled={saving}>
              {saving ? (
                <>
                  <span className="events-btn__spinner" />
                  {t("eventsSaving")}
                </>
              ) : (
                <>
                  <i className="fa-solid fa-check" />
                  {editingEvent ? t("eventsSave") : t("eventsCreate")}
                </>
              )}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function Events({ archived = false, title }) {
  const { t, i18n } = useTranslation();
  const pageTitle = title || (archived ? t("eventsArchivedPageTitle") : t("eventsPageTitle"));
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";

  const [events, setEvents] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [menuFilter, setMenuFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [detailsEvent, setDetailsEvent] = useState(null);
  const [shareEvent, setShareEvent] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [orderSaving, setOrderSaving] = useState(false);
  const [openStatusEventId, setOpenStatusEventId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState("");

  const menuLabelById = useMemo(() => {
    const map = new Map();
    menus.forEach((menu) => map.set(String(menu._id), getMenuLabel(menu, lang)));
    return map;
  }, [menus, lang]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsData, menusData] = await Promise.all([
        fetchAllEvents({ page: 1, limit: 500, archived, search }),
        fetchAllMenus(),
      ]);
      setEvents(Array.isArray(eventsData?.events) ? eventsData.events : []);
      setMenus(Array.isArray(menusData) ? menusData : []);
    } catch (error) {
      console.error("Error loading events:", error);
      Swal.fire(t("eventsError"), error?.response?.data?.message || error.message || t("eventsErrorLoad"), "error");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [archived, search, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const stats = useMemo(() => {
    if (archived) {
      const withMenus = events.filter((event) => getEventMenuIds(event).length > 0).length;
      return { total: events.length, withMenus, active: 0, upcoming: 0, full: 0 };
    }
    return {
      total: events.length,
      active: events.filter(isEventLive).length,
      upcoming: events.filter(isEventUpcoming).length,
      full: events.filter(isEventFullStatus).length,
    };
  }, [events, archived]);

  const sortedEvents = useMemo(() => sortEventsByPriority(events), [events]);

  const filteredEvents = useMemo(() => {
    return sortedEvents.filter((event) => {
      if (statusFilter === "upcoming" && !isEventUpcoming(event)) return false;
      if (statusFilter !== "all" && statusFilter !== "upcoming" && getEventDisplayStatusKey(event) !== statusFilter) {
        return false;
      }
      if (menuFilter && !getEventMenuIds(event).includes(String(menuFilter))) return false;
      return true;
    });
  }, [sortedEvents, statusFilter, menuFilter]);

  const hasActiveFilters = statusFilter !== "all" || Boolean(menuFilter);
  const canReorder = !archived && !hasActiveFilters && !search.trim();

  const clearFilters = () => {
    setStatusFilter("all");
    setMenuFilter("");
    setCurrentPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / PAGE_SIZE));
  const visibleEvents = filteredEvents.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE
  );

  const toGlobalIndex = useCallback(
    (pageIndex) => currentPage * PAGE_SIZE + pageIndex,
    [currentPage]
  );

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, Math.max(0, totalPages - 1)));
  }, [totalPages]);

  useEffect(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [currentPage]);

  const handleDragStart = (e, pageIndex) => {
    if (!canReorder || orderSaving) return;
    setDraggedIndex(toGlobalIndex(pageIndex));
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(toGlobalIndex(pageIndex)));
  };

  const handleDragOver = (e, pageIndex) => {
    if (!canReorder || orderSaving) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(toGlobalIndex(pageIndex));
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = async (e, pageIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    const fromIndex = draggedIndex;
    const dropIndex = toGlobalIndex(pageIndex);
    setDraggedIndex(null);
    if (!canReorder || fromIndex == null || fromIndex === dropIndex) return;

    const movedEvent = filteredEvents[fromIndex];
    const nextSorted = [...filteredEvents];
    const [removed] = nextSorted.splice(fromIndex, 1);
    nextSorted.splice(dropIndex, 0, removed);
    let nextPriority = nextSorted.filter((event) => !event.isSpotlight).length;
    const withPriority = nextSorted.map((event) => {
      if (event.isSpotlight) {
        return { ...event, priority: 0 };
      }
      const assigned = nextPriority;
      nextPriority -= 1;
      return { ...event, priority: assigned };
    });

    const displayRank = movedEvent?.isSpotlight
      ? 0
      : nextSorted.slice(0, dropIndex).filter((item) => !item.isSpotlight).length + 1;

    const result = await Swal.fire({
      title: t("eventsOrderConfirm"),
      html: t("eventsOrderConfirmText", {
        title: movedEvent?.title || "",
        rank: displayRank,
      }),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("eventsOrderConfirmAction"),
      cancelButtonText: t("eventsCancel"),
    });
    if (!result.isConfirmed) return;

    const previousEvents = events;
    setEvents(withPriority);
    setOrderSaving(true);
    try {
      await updateEventsOrder(withPriority.map((event) => event._id));
      Swal.fire({
        icon: "success",
        title: t("eventsOrderUpdated"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      setEvents(previousEvents);
      Swal.fire(
        t("eventsError"),
        error?.response?.data?.message || error.message || t("eventsOrderUpdateError"),
        "error"
      );
    } finally {
      setOrderSaving(false);
    }
  };

  const openCreateModal = () => {
    setEditingEvent(null);
    setForm({ ...EMPTY_FORM, menuFKs: [], clientPosition: "" });
    setModalOpen(true);
  };

  const openEditModal = (event) => {
    const recurrence = getRecurrenceFormFields(event);
    setEditingEvent(event);
    setForm({
      title: event.title || "",
      message: event.message || "",
      capacity: event.capacity != null && Number(event.capacity) > 0 ? String(event.capacity) : "",
      menuFKs: (event.menuFKs || []).map((m) => (typeof m === "object" ? m._id : m)).filter(Boolean),
      startDate: toDatetimeLocal(event.startDate),
      endDate: toDatetimeLocal(event.endDate),
      publishBeforeAmount: Number(event.publishBeforeAmount) || 0,
      publishBeforeUnit: normalizePublishBeforeUnit(event.publishBeforeUnit),
      publishUntil: toDatetimeLocal(event.publishUntil || event.endDate),
      isRecurring: Boolean(event.isRecurring),
      recurrenceType: recurrence.recurrenceType,
      recurrenceWeekday: getRecurrenceWeekday(event),
      recurrenceMonthDay: getRecurrenceMonthDay(event),
      recurrenceMonths: recurrence.recurrenceMonths,
      recurrenceMonthDays: recurrence.recurrenceMonthDays,
      isSpotlight: Boolean(event.isSpotlight),
      isActive: event.isActive !== false,
      clientPosition: getClientPositionForEvent(event, events),
    });
    setModalOpen(true);
  };

  const openDetailsModal = (event) => {
    setDetailsEvent(event);
  };

  const closeDetailsModal = () => {
    setDetailsEvent(null);
  };

  const openShareModal = (event) => {
    setShareEvent(event);
  };

  const closeShareModal = () => {
    setShareEvent(null);
  };

  const openEditFromDetails = (event) => {
    closeDetailsModal();
    openEditModal(event);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingEvent(null);
  };

  const getEventMenuEntries = (event) => {
    const ids = (event.menuFKs || []).map((m) => (typeof m === "object" ? m._id : m)).filter(Boolean);
    return ids.map((id) => {
      const populated = (event.menuFKs || []).find((m) => String(m?._id || m) === String(id));
      const label =
        populated && typeof populated === "object" && populated.menuDetails
          ? getMenuLabel(populated, lang)
          : menuLabelById.get(String(id)) || t("eventsUnknownMenu");
      return { id: String(id), label };
    });
  };

  const handleEventSaved = useCallback(
    (savedEvent) => {
      if (savedEvent?._id) {
        setEvents((prev) => {
          const exists = prev.some((item) => item._id === savedEvent._id);
          const merged = exists
            ? prev.map((item) => (item._id === savedEvent._id ? { ...item, ...savedEvent } : item))
            : [savedEvent, ...prev];
          if (!savedEvent.isSpotlight) return merged;
          return merged.map((item) =>
            item._id === savedEvent._id
              ? { ...item, priority: 0 }
              : { ...item, isSpotlight: false, spotlightAt: null }
          );
        });
        setCurrentPage(0);
        setDetailsEvent((prev) =>
          prev?._id === savedEvent._id ? { ...prev, ...savedEvent } : prev
        );
        return;
      }
      loadData();
    },
    [loadData]
  );

  const handleArchiveToggle = async (event) => {
    const nextArchived = !archived;
    const result = await Swal.fire({
      title: nextArchived ? t("eventsArchiveConfirm") : t("eventsRestoreConfirm"),
      text: nextArchived ? t("eventsArchiveConfirmText") : t("eventsRestoreConfirmText"),
      icon: nextArchived ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: nextArchived ? t("eventsArchiveAction") : t("eventsRestoreAction"),
      cancelButtonText: t("eventsCancel"),
    });
    if (!result.isConfirmed) return;
    try {
      await updateEventArchive(event._id, nextArchived);
      setEvents((prev) => prev.filter((item) => item._id !== event._id));
      Swal.fire(t("eventsDone"), t("eventsDoneText"), "success");
    } catch (error) {
      Swal.fire(t("eventsError"), error?.response?.data?.message || error.message, "error");
    }
  };

  const mergeEventAfterStatusUpdate = (item, updated, changes) => {
    const next = resolveEventPlacesForDisplay(
      {
        ...item,
        ...updated,
        confirmedGuests: item.confirmedGuests,
        capacity: item.capacity ?? updated.capacity,
      },
      changes
    );
    next.isFull = isEventFull(next);
    return next;
  };

  const handleEventStatusChange = async (event, field, value) => {
    let payload = buildEventStatusPayload(field, value);

    if (field === "availability" && value === "full") {
      const choice = await promptEventFullClientOptions(t, {
        clientShowPlacesWhenFull: event.clientShowPlacesWhenFull,
        clientShowReserveWhenFull: event.clientShowReserveWhenFull,
      });
      if (!choice.confirmed) return;
      payload = {
        ...payload,
        clientShowPlacesWhenFull: choice.clientShowPlacesWhenFull,
        clientShowReserveWhenFull: choice.clientShowReserveWhenFull,
      };
    } else {
      const statusLabel = getEventStatusOptionLabel(field, value, t);
      const currentMeta = getEventStatusMeta(event, archived, t);
      const result = await confirmEventStatusChange(t, {
        title: event.title || "",
        statusLabel,
        currentLabel: currentMeta.label,
      });
      if (!result.isConfirmed) return;

      if (field === "availability" && value === "auto") {
        payload = { ...payload, ...resetEventFullClientOptions() };
      }
    }

    setUpdatingStatusId(event._id);
    try {
      const result = await updateEvent(event._id, payload);
      const updated = result?.data || payload;
      const changes = payload;
      setEvents((prev) =>
        prev.map((item) => (item._id === event._id ? mergeEventAfterStatusUpdate(item, updated, changes) : item))
      );
      setDetailsEvent((prev) =>
        prev?._id === event._id ? mergeEventAfterStatusUpdate(prev, updated, changes) : prev
      );
      showEventActionSuccess(t, {
        title: t("eventsStatusUpdated"),
        text: t("eventsStatusUpdatedText"),
      });
    } catch (error) {
      Swal.fire(t("eventsError"), error?.response?.data?.message || error.message, "error");
    } finally {
      setUpdatingStatusId("");
    }
  };

  const handleDelete = async (event) => {
    const result = await Swal.fire({
      title: t("eventsDeleteConfirm"),
      text: t("eventsDeleteConfirmText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("eventsDeleteAction"),
      cancelButtonText: t("eventsCancel"),
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;
    try {
      await deleteEvent(event._id);
      setEvents((prev) => prev.filter((item) => item._id !== event._id));
      Swal.fire(t("eventsDeleted"), t("eventsDeletedText"), "success");
    } catch (error) {
      Swal.fire(t("eventsError"), error?.response?.data?.message || error.message, "error");
    }
  };

  const handleRepublish = async (event) => {
    const nextStart = new Date(event.startDate);
    const nextEnd = new Date(event.endDate);
    const duration = nextEnd.getTime() - nextStart.getTime();
    const weekday = getRecurrenceWeekday(event);
    let candidate = new Date(event.endDate);
    candidate.setDate(candidate.getDate() + 1);
    candidate.setHours(nextStart.getHours(), nextStart.getMinutes(), 0, 0);
    for (let guard = 0; guard < 8; guard += 1) {
      if (candidate.getDay() === weekday) break;
      candidate.setDate(candidate.getDate() + 1);
    }
    const previewEnd = new Date(candidate.getTime() + duration);

    const result = await Swal.fire({
      title: t("eventsRepublishConfirm"),
      html: t("eventsRepublishConfirmText", {
        period: formatDateRange(candidate, previewEnd),
        count: (Number(event.occurrenceCount) || 1) + 1,
      }),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("eventsActionRepublish"),
      cancelButtonText: t("eventsCancel"),
    });
    if (!result.isConfirmed) return;

    try {
      const response = await republishEvent(event._id);
      const savedEvent = response?.data;
      if (savedEvent?._id) {
        setEvents((prev) => prev.map((item) => (item._id === savedEvent._id ? { ...item, ...savedEvent } : item)));
      } else {
        await loadData();
      }
      Swal.fire(t("eventsRepublished"), t("eventsRepublishedText"), "success");
    } catch (error) {
      Swal.fire(t("eventsError"), error?.response?.data?.message || error.message, "error");
    }
  };

  return (
    <div className={`events-page${archived ? " events-page--archived" : ""}`} data-tour="tour-page-events">
      <div className="events-hero">
        <div className="events-hero__glow" aria-hidden />
        <div className="events-hero__main">
          <div className="events-hero__content">
            <span className="events-hero__icon">
              <i className={archived ? "fa-solid fa-box-archive" : "fa-regular fa-calendar-days"} />
            </span>
            <div>
              <h1 className="events-hero__title">{pageTitle}</h1>
              <p className="events-hero__subtitle">
                {archived ? t("eventsArchivedSubtitle") : t("eventsPageSubtitle")}
              </p>
            </div>
          </div>
          {!archived ? (
            <button type="button" className="events-btn events-btn--success events-btn--hero" onClick={openCreateModal}>
              <i className="fa-solid fa-plus" />
              {t("eventsAddEvent")}
            </button>
          ) : (
            <Link to="/events" className="events-btn events-btn--ghost events-btn--hero events-back-link">
              <i className="fa-solid fa-arrow-left" />
              {t("eventsViewActiveEvents")}
            </Link>
          )}
        </div>

        <div className={`events-kpi-grid${archived ? " events-kpi-grid--archived" : " events-kpi-grid--4"}`}>
          {archived ? (
            <>
              <div className="events-kpi events-kpi--archived events-kpi--stat">
                <span className="events-kpi__icon"><i className="fa-solid fa-box-archive" /></span>
                <div className="events-kpi__label">{t("eventsKpiArchived")}</div>
                <div className="events-kpi__value">{stats.total}</div>
              </div>
              <div className="events-kpi events-kpi--archived-muted events-kpi--stat">
                <span className="events-kpi__icon"><i className="fa-solid fa-utensils" /></span>
                <div className="events-kpi__label">{t("eventsKpiWithMenus")}</div>
                <div className="events-kpi__value">{stats.withMenus}</div>
              </div>
            </>
          ) : (
          <button
            type="button"
            className={`events-kpi events-kpi--total events-kpi--clickable${statusFilter === "all" ? " is-active" : ""}`}
            onClick={() => {
              setStatusFilter("all");
              setCurrentPage(0);
            }}
          >
            <span className="events-kpi__icon"><i className="fa-solid fa-layer-group" /></span>
            <div className="events-kpi__label">{t("eventsKpiTotal")}</div>
            <div className="events-kpi__value">{stats.total}</div>
          </button>
          )}
          {!archived ? (
            <>
              <button
                type="button"
                className={`events-kpi events-kpi--active events-kpi--clickable${statusFilter === "live" ? " is-active" : ""}`}
                onClick={() => {
                  setStatusFilter("live");
                  setCurrentPage(0);
                }}
              >
                <span className="events-kpi__icon"><i className="fa-solid fa-circle-play" /></span>
                <div className="events-kpi__label">{t("eventsKpiLive")}</div>
                <div className="events-kpi__value">{stats.active}</div>
              </button>
              <button
                type="button"
                className={`events-kpi events-kpi--scheduled events-kpi--clickable${statusFilter === "upcoming" ? " is-active" : ""}`}
                onClick={() => {
                  setStatusFilter("upcoming");
                  setCurrentPage(0);
                }}
              >
                <span className="events-kpi__icon"><i className="fa-regular fa-clock" /></span>
                <div className="events-kpi__label">{t("eventsKpiScheduled")}</div>
                <div className="events-kpi__value">{stats.upcoming}</div>
              </button>
              <button
                type="button"
                className={`events-kpi events-kpi--full events-kpi--clickable${statusFilter === "full" ? " is-active" : ""}`}
                onClick={() => {
                  setStatusFilter("full");
                  setCurrentPage(0);
                }}
              >
                <span className="events-kpi__icon"><i className="fa-solid fa-users-slash" /></span>
                <div className="events-kpi__label">{t("eventsKpiFull")}</div>
                <div className="events-kpi__value">{stats.full}</div>
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="events-filters">
        <div className={`events-filters__bar${hasActiveFilters ? " events-filters__bar--has-clear" : ""}${archived ? " events-filters__bar--archived" : ""}`}>
          <div className="events-filters__zone events-filters__zone--search">
            <span className="events-filters__label">{t("eventsFilterSearch")}</span>
            <div className="events-search events-search--bar">
              <i className="fa-solid fa-magnifying-glass" />
              <input
                type="search"
                placeholder={t("eventsSearchPlaceholder")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>

          {archived ? (
            <div className="events-filters__zone events-filters__zone--menu">
              <label className="events-filters__label" htmlFor="events-archived-menu-filter">
                {t("eventsFilterMenu")}
              </label>
              <div className="events-filter-select-wrap">
                <i className="fa-solid fa-utensils" aria-hidden />
                <select
                  id="events-archived-menu-filter"
                  className="events-filter-select"
                  value={menuFilter}
                  onChange={(e) => {
                    setMenuFilter(e.target.value);
                    setCurrentPage(0);
                  }}
                >
                  <option value="">{t("eventsFilterAllMenus")}</option>
                  {menus.map((menu) => (
                    <option key={menu._id} value={menu._id}>
                      {getMenuLabel(menu, lang)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          {!archived ? (
            <>
              <div className="events-filters__zone events-filters__zone--status">
                <label className="events-filters__label" htmlFor="events-status-filter">
                  {t("eventsFilterStatus")}
                </label>
                <div className="events-filter-select-wrap">
                  <i className="fa-solid fa-filter" aria-hidden />
                  <select
                    id="events-status-filter"
                    className="events-filter-select"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(0);
                    }}
                  >
                    {EVENT_STATUS_FILTER_OPTIONS.map(({ id, labelKey }) => (
                      <option key={id} value={id}>
                        {t(labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="events-filters__zone events-filters__zone--menu">
                <label className="events-filters__label" htmlFor="events-menu-filter">
                  {t("eventsFilterMenu")}
                </label>
                <div className="events-filter-select-wrap">
                  <i className="fa-solid fa-utensils" aria-hidden />
                  <select
                    id="events-menu-filter"
                    className="events-filter-select"
                    value={menuFilter}
                    onChange={(e) => {
                      setMenuFilter(e.target.value);
                      setCurrentPage(0);
                    }}
                  >
                    <option value="">{t("eventsFilterAllMenus")}</option>
                    {menus.map((menu) => (
                      <option key={menu._id} value={menu._id}>
                        {getMenuLabel(menu, lang)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          ) : null}

          {hasActiveFilters ? (
            <div className="events-filters__zone events-filters__zone--clear">
              <span className="events-filters__label events-filters__label--hidden" aria-hidden>
                —
              </span>
              <button type="button" className="events-filter-clear" onClick={clearFilters}>
                <i className="fa-solid fa-xmark" />
                {t("eventsFilterClear")}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className={`events-card${archived ? " events-card--archived" : ""}`}>
        {loading ? (
          <div className="events-loading">
            <span className="events-loading__spinner" />
            {t("eventsLoading")}
          </div>
        ) : visibleEvents.length === 0 ? (
          <div className={`events-empty${archived ? " events-empty--archived" : ""}`}>
            <i className={`${archived ? "fa-solid fa-box-open" : "fa-regular fa-calendar-plus"} d-block`} />
            <h4>{hasActiveFilters ? t("eventsEmptyFiltered") : archived ? t("eventsEmptyArchived") : t("eventsEmptyTitle")}</h4>
            <p>{hasActiveFilters ? t("eventsEmptyFilteredText") : archived ? t("eventsEmptyArchivedText") : t("eventsEmptyText")}</p>
            {hasActiveFilters ? (
              <button type="button" className="events-btn events-btn--ghost events-empty__cta" onClick={clearFilters}>
                {t("eventsFilterClear")}
              </button>
            ) : null}
            {!archived && !hasActiveFilters ? (
              <button type="button" className="events-btn events-btn--success events-empty__cta" onClick={openCreateModal}>
                <i className="fa-solid fa-plus" />
                {t("eventsAddEvent")}
              </button>
            ) : null}
            {archived && !hasActiveFilters && !searchInput ? (
              <Link to="/events" className="events-btn events-btn--ghost events-empty__cta">
                <i className="fa-solid fa-arrow-left" />
                {t("eventsViewActiveEvents")}
              </Link>
            ) : null}
          </div>
        ) : (
          <>
            <div className={`events-table-card__head${archived ? " events-table-card__head--archived" : ""}`}>
              <span className="events-table-card__title">
                <i className={archived ? "fa-solid fa-box-archive" : "fa-regular fa-calendar-check"} />
                {archived ? t("eventsArchivedPageTitle") : t("eventsPageTitle")}
              </span>
              <span className={`events-table-card__count${archived ? " events-table-card__count--archived" : ""}`}>
                {t("eventsTableCount", { count: filteredEvents.length })}
              </span>
            </div>
            {canReorder ? (
              <p className="events-order-hint">
                <i className="fa-solid fa-grip-vertical" aria-hidden />
                {t("eventsDragToReorder")}
              </p>
            ) : null}
            <div className="events-table-wrap">
              <table className={`events-table events-table--modern${archived ? " events-table--archived" : ""}${canReorder ? " events-table--sortable" : ""}`}>
                <thead>
                  <tr>
                    {canReorder ? <th className="events-table__order-col">{t("eventsColOrder")}</th> : null}
                    <th>{t("eventsColEvent")}</th>
                    <th>{t("eventsColMenus")}</th>
                    <th>{t("eventsColPeriod")}</th>
                    <th>{t("eventsColStatus")}</th>
                    <th>{t("eventsColReservations")}</th>
                    <th>{t("eventsColActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEvents.map((event, index) => {
                    const menuEntries = getEventMenuEntries(event);
                    const visibleMenuEntries = menuEntries.slice(0, TABLE_MENUS_PREVIEW_LIMIT);
                    const hiddenMenuEntries = menuEntries.slice(TABLE_MENUS_PREVIEW_LIMIT);
                    const globalIndex = toGlobalIndex(index);
                    const displayRank = event.isSpotlight
                      ? 0
                      : canReorder
                        ? filteredEvents.slice(0, globalIndex).filter((item) => !item.isSpotlight).length + 1
                        : currentPage * PAGE_SIZE +
                          visibleEvents.slice(0, index).filter((item) => !item.isSpotlight).length +
                          1;
                    return (
                    <tr
                      key={event._id}
                      className={`events-table-row${archived ? " events-table-row--archived" : ""}${canReorder ? " events-table-row--sortable" : ""}${draggedIndex === globalIndex ? " is-dragging" : ""}${dragOverIndex === globalIndex ? " is-drop-target" : ""}`}
                      draggable={canReorder && !orderSaving}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      {canReorder ? (
                        <td className="events-table__order">
                          <div className="events-order-cell">
                            <span className="events-order-cell__handle" title={t("eventsDragToReorder")}>
                              <i className="fa-solid fa-grip-vertical" aria-hidden />
                            </span>
                            <span className="events-order-cell__rank">{displayRank}</span>
                          </div>
                        </td>
                      ) : null}
                      <td className="events-table__event">
                        <div className="events-event-cell">
                          <EventThumb imageUrl={event.imageUrl} />
                          <div className="events-event-cell__body">
                            <div className="events-event-title">
                              {event.title}
                              {event.isSpotlight ? (
                                <span className="events-spotlight-badge" title={t("eventsSpotlightBadge")}>
                                  <i className="fa-solid fa-star" aria-hidden />
                                  {t("eventsSpotlightBadge")}
                                </span>
                              ) : null}
                            </div>
                            <div className="events-event-message">{event.message}</div>
                            {event.isRecurring ? (
                              <div className="events-event-meta">
                                <span className="events-recurrence-badge">
                                  <i className="fa-solid fa-rotate" aria-hidden />
                                  {formatRecurrenceLabel(event, t)}
                                </span>
                                <span
                                  className={`events-occurrence-badge${isHighOccurrence(event) ? " events-occurrence-badge--high" : ""}`}
                                  title={isHighOccurrence(event) ? t("eventsOccurrenceHighHint") : undefined}
                                >
                                  {t("eventsOccurrenceCount", { count: Number(event.occurrenceCount) || 1 })}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="events-table__menus">
                        {menuEntries.length === 0 ? (
                          <span className="events-table__empty">—</span>
                        ) : (
                          <div className="events-menu-chips">
                            {visibleMenuEntries.map((menu) => (
                              <Link key={menu.id} to={`/edit-menu/${menu.id}`} className="events-menu-chip">
                                <i className="fa-solid fa-utensils" aria-hidden />
                                <span>{menu.label}</span>
                              </Link>
                            ))}
                            {hiddenMenuEntries.length > 0 ? (
                              <span
                                className="events-menu-more"
                                title={hiddenMenuEntries.map((menu) => menu.label).join(", ")}
                                aria-label={t("eventsMenusMore", { count: hiddenMenuEntries.length })}
                              >
                                ...
                              </span>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className="events-table__period">
                        <div className="events-period-cell">
                          <i className="fa-regular fa-calendar-days" aria-hidden />
                          <span>{formatDateRange(event.startDate, event.endDate)}</span>
                        </div>
                      </td>
                      <td className="events-table__status">
                        <EventStatusPicker
                          event={event}
                          archived={archived}
                          t={t}
                          isOpen={openStatusEventId === event._id}
                          isUpdating={updatingStatusId === event._id}
                          onToggle={(open) => setOpenStatusEventId(open ? event._id : null)}
                          onStatusChange={handleEventStatusChange}
                        />
                      </td>
                      <td className="events-table__reservations">
                        <Link
                          to={`/events/${event._id}/reservations`}
                          className="events-reservations-btn"
                          title={t("eventsReservationsBtn")}
                        >
                          <i className="fa-solid fa-calendar-check" aria-hidden />
                          <span>{t("eventsReservationsBtn")}</span>
                          {Number(event.reservationCount) > 0 ? (
                            <span className="events-reservations-btn__count">{event.reservationCount}</span>
                          ) : null}
                        </Link>
                      </td>
                      <td className="events-table__actions">
                        <div className="events-row-actions">
                          {!archived ? (
                            <>
                              <button
                                type="button"
                                className="events-action-btn events-action-btn--view"
                                title={t("eventsActionView")}
                                onClick={() => openDetailsModal(event)}
                              >
                                <i className="fa-solid fa-eye" />
                              </button>
                              <button
                                type="button"
                                className="events-action-btn events-action-btn--share"
                                title={t("eventsActionShare")}
                                onClick={() => openShareModal(event)}
                              >
                                <i className="fa-solid fa-share-nodes" />
                              </button>
                              <button
                                type="button"
                                className="events-action-btn events-action-btn--edit"
                                title={t("eventsActionEdit")}
                                onClick={() => openEditModal(event)}
                              >
                                <i className="fa-solid fa-pen" />
                              </button>
                              {canRepublishEvent(event) ? (
                                <button
                                  type="button"
                                  className="events-action-btn events-action-btn--republish"
                                  title={t("eventsActionRepublish")}
                                  onClick={() => handleRepublish(event)}
                                >
                                  <i className="fa-solid fa-rotate" />
                                </button>
                              ) : null}
                              <button type="button" className="events-action-btn events-action-btn--archive" title={t("eventsActionArchive")} onClick={() => handleArchiveToggle(event)}>
                                <i className="fa-solid fa-box-archive" />
                              </button>
                            </>
                          ) : (
                            <div className="events-row-actions events-row-actions--archived">
                              <button
                                type="button"
                                className="events-action-btn events-action-btn--view"
                                title={t("eventsActionView")}
                                onClick={() => openDetailsModal(event)}
                              >
                                <i className="fa-solid fa-eye" />
                              </button>
                              <button
                                type="button"
                                className="events-action-btn events-action-btn--share"
                                title={t("eventsActionShare")}
                                onClick={() => openShareModal(event)}
                              >
                                <i className="fa-solid fa-share-nodes" />
                              </button>
                              <button type="button" className="events-archived-action events-archived-action--restore" onClick={() => handleArchiveToggle(event)}>
                                <i className="fa-solid fa-rotate-left" aria-hidden />
                                <span>{t("eventsActionRestore")}</span>
                              </button>
                              <button type="button" className="events-archived-action events-archived-action--delete" onClick={() => handleDelete(event)}>
                                <i className="fa-solid fa-trash" aria-hidden />
                                <span>{t("eventsActionDelete")}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="events-pagination">
              <div className="events-pagination__info">
                {t("eventsPagination", {
                  from: filteredEvents.length === 0 ? 0 : currentPage * PAGE_SIZE + 1,
                  to: Math.min((currentPage + 1) * PAGE_SIZE, filteredEvents.length),
                  total: filteredEvents.length,
                })}
              </div>
              <div className="events-pagination__buttons">
                <button type="button" className="events-page-btn" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}>
                  <i className="fa-solid fa-chevron-left" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i} type="button" className={`events-page-btn${currentPage === i ? " is-active" : ""}`} onClick={() => setCurrentPage(i)}>
                    {i + 1}
                  </button>
                ))}
                <button type="button" className="events-page-btn" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}>
                  <i className="fa-solid fa-chevron-right" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <EventDetailsModal
        open={Boolean(detailsEvent)}
        event={detailsEvent}
        archived={archived}
        menuEntries={detailsEvent ? getEventMenuEntries(detailsEvent) : []}
        eventsList={events}
        onClose={closeDetailsModal}
        onEdit={openEditFromDetails}
        t={t}
      />

      <EventShareModal
        open={Boolean(shareEvent)}
        event={shareEvent}
        onClose={closeShareModal}
        t={t}
      />

      <EventFormModal
        open={modalOpen}
        editingEvent={editingEvent}
        initialForm={form}
        menus={menus}
        maxClientPosition={getMaxClientPosition(events, Boolean(editingEvent))}
        onClose={closeModal}
        onSaved={handleEventSaved}
        t={t}
        i18n={i18n}
      />
    </div>
  );
}

export default Events;
